/**
 * Language Context for Multi-Language Support
 * Supports: English, Spanish, French, Portuguese, German, Chinese, Japanese, Korean
 */
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import i18nInstance from "../i18n";  // i18next bridge — see t() below
import VENDOR_PORTAL_I18N from "./vendorPortalI18n";  // Phase 7g.15.36 — vendor portal simplification keys
import ADMIN_FRONT_PAGE_I18N from "./adminFrontPageI18n";  // Phase 7g.15.48 — admin front-page + Control Center keys

// Translation data
const translations = {
  en: {
    // Header
    nav_services: "Services",
    nav_how_it_works: "How It Works",
    nav_vendors: "For Vendors",
    nav_pricing: "Pricing",
    nav_login: "Login",
    nav_get_started: "Get Started",
    
    // Hero Section
    hero_tagline: "WAX AND SHINE HEADQUARTERS™",
    hero_subtitle: "THE CARWASH CONCIERGE COMPANY™",
    hero_title_1: "Premium Mobile",
    hero_title_2: "Car Wash",
    hero_title_3: "On Demand",
    hero_description: "Experience the future of car care. Professional detailing services delivered to your doorstep by verified, insured vendors.",
    hero_cta: "Book Your Wash",
    hero_watch_demo: "Watch Demo",
    hero_feature_1: "5-Star Rated Vendors",
    hero_feature_2: "Insured & Verified",
    hero_feature_3: "On-Demand Service",
    
    // Services Section
    services_title: "Our Services",
    services_subtitle: "Premium car care solutions for every vehicle type",
    service_car_wash: "Premium Car Wash",
    service_car_wash_desc: "Professional mobile detailing at your doorstep",
    service_boat: "Boat Detailing",
    service_boat_desc: "Marine-grade cleaning for all watercraft",
    service_rv: "RV Washing",
    service_rv_desc: "Full-service RV and motorhome cleaning",
    service_fleet: "Fleet Management",
    service_fleet_desc: "Commercial fleet washing solutions",
    
    // How It Works
    how_it_works_title: "How It Works",
    how_it_works_subtitle: "Get your vehicle sparkling clean in 3 easy steps",
    step_1_title: "Book Online",
    step_1_desc: "Choose your service, vehicle, and preferred time slot",
    step_2_title: "We Come to You",
    step_2_desc: "Our verified vendor arrives at your location",
    step_3_title: "Enjoy the Shine",
    step_3_desc: "Relax while we transform your vehicle",
    
    // For Vendors
    vendors_title: "For Vendors",
    vendors_subtitle: "Join our network of professional detailers",
    vendors_benefit_1: "Set Your Own Schedule",
    vendors_benefit_1_desc: "Work when you want, where you want",
    vendors_benefit_2: "Grow Your Business",
    vendors_benefit_2_desc: "Access thousands of customers in your area",
    vendors_benefit_3: "Get Paid Fast",
    vendors_benefit_3_desc: "Weekly payouts directly to your account",
    vendors_cta: "Become a Vendor",
    vendors_pricing: "View Service Pricing",
    vendors_watch_tutorial: "Watch Tutorial Video",
    
    // Pricing Section
    pricing_title: "Service Pricing Guide",
    pricing_subtitle: "Starting prices for all vehicle types. Vendors set their own rates - find the best price for your needs.",
    pricing_car: "Car / Sedan",
    pricing_car_types: "Coupes, Sedans, Hatchbacks",
    pricing_suv: "SUV / Truck",
    pricing_suv_types: "SUVs, Trucks, Vans",
    pricing_boat: "Boats",
    pricing_boat_types: "All watercraft types",
    pricing_rv: "RVs",
    pricing_rv_types: "Motorhomes & Campers",
    pricing_starting: "Starting at",
    pricing_per_ft: "/ft over",
    
    // Add-ons
    addons_title: "Popular Add-On Services",
    addon_vacuum: "Interior Vacuum",
    addon_leather: "Leather Care",
    addon_wax: "Wax Sealant",
    addon_pet_hair: "Pet Hair Removal",
    pricing_disclaimer: "*Starting prices shown. Vendors set their own rates. A 20% Network Service Fee applies to all bookings.",
    
    // Vendor Pricing Note
    vendor_pricing_title: "Vendors Set Their Own Prices",
    vendor_pricing_desc: "Each vendor can customize their pricing. Compare vendors to find the best rates in your area.",
    vendor_pricing_tip: "Pro tip: Competitive pricing attracts more customers - if prices are too high, customers may choose a more affordable option.",
    vendor_pricing_cta: "Vendor Pricing Dashboard",
    
    // Booking Policy
    policy_title: "Booking & Cancellation Policy",
    policy_arrival_title: "Vendor Arrival Expectations",
    policy_arrival_1: "Vendors must arrive within 30 minutes of the scheduled appointment time",
    policy_arrival_2: "If running late, vendors are required to provide constant status updates to the customer",
    policy_arrival_3: "Real-time tracking available once vendor is en route",
    policy_cancel_title: "Customer Cancellation Policy",
    policy_cancel_free: "Free cancellation before vendor accepts booking",
    policy_cancel_fee: "20% cancellation fee if cancelled after vendor is en route or within 20 minutes of accepted booking",
    policy_cancel_brick: "Same policy applies to brick & mortar shop appointments",
    policy_why: "Why this policy?",
    policy_why_desc: "We value both our vendors' and customers' time. This policy protects vendors from wasted trips and fuel costs while encouraging customers to honor their scheduled appointments.",
    
    // Membership
    membership_title: "Customer Membership",
    membership_subtitle: "Join free or upgrade to VIP for extra perks",
    membership_free: "Free",
    membership_free_desc: "Perfect for occasional washes",
    membership_vip: "VIP Member",
    membership_vip_desc: "For car care enthusiasts",
    membership_feature_booking: "Easy online booking",
    membership_feature_vendors: "Access to all vendors",
    membership_feature_tracking: "Real-time tracking",
    membership_feature_support: "Customer support",
    membership_feature_priority: "Priority booking",
    membership_feature_discount: "10% off all services",
    membership_feature_rewards: "Exclusive rewards",
    membership_feature_deals: "Early access to deals",
    membership_cta_free: "Get Started Free",
    membership_cta_vip: "Join VIP",
    
    // Footer
    footer_pay_per_wash: "Pay only when you wash",
    footer_secure: "Secure payments",
    footer_platform_fee: "Platform fee:",
    footer_vip_video: "See VIP Benefits in Action",
    
    // Portal Selection
    portal_title: "CHOOSE YOUR PORTAL",
    portal_customer: "Customer",
    portal_vendor: "Vendor",
    portal_admin: "Admin",
    
    // Common
    learn_more: "Learn More",
    contact_us: "Contact Us",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    per_month: "/month",
    recommended: "Recommended",
    
    // Service History & Recurring
    service_history: "Service History",
    past_bookings: "Your Past Bookings",
    recurring_bookings: "Recurring Bookings",
    no_bookings: "No bookings yet",
    rebook_service: "Rebook This Service",
    make_recurring: "Make Recurring",
    add_tip: "Add Tip",
    tip_added: "Tip Added",
    every_x_days: "Every {days} days",
    pause: "Pause",
    resume: "Resume",
    cancel_recurring: "Cancel Recurring",
    
    // Tip Collection
    add_a_tip: "Add a Tip",
    show_appreciation: "Show Your Appreciation",
    tip_goes_to_vendor: "100% of your tip goes directly to",
    send_tip: "Send Tip",
    skip_for_now: "Skip for now",
    thank_you: "Thank You!",
    tip_sent: "Your tip has been sent",
    
    // Fleet Contact
    fleet_management: "Fleet Management",
    get_custom_pricing: "Get custom pricing for your business",
    company_info: "Company Information",
    company_name: "Company Name",
    contact_name: "Contact Name",
    fleet_details: "Fleet Details",
    vehicle_types: "Vehicle Types in Fleet",
    fleet_size: "Fleet Size",
    service_frequency: "Desired Service Frequency",
    submit_inquiry: "Submit Fleet Inquiry",
    inquiry_submitted: "Inquiry Submitted!",
    
    // Shop Finder
    find_shop: "Find a Shop",
    nearby_shops: "Nearby Car Wash Shops",
    book_appointment: "Book Appointment",
    select_date: "Select Date",
    select_time: "Select Time",
    available_slots: "Available Slots",
    confirm_booking: "Confirm Booking",
    
    // Admin Dashboard
    admin_dashboard: "Admin Dashboard",
    admin_master_access: "Master Admin Access",
    admin_full_control: "Full control over WashPros platform",
    admin_service_map: "Service Map",
    admin_crm: "CRM",
    admin_revenue: "Revenue",
    admin_pricing: "Pricing",
    admin_surge_config: "Surge Config",
    admin_surge_analytics: "Surge Analytics",
    admin_leaderboard: "Leaderboard",
    admin_insurance: "Insurance",
    admin_agreements: "Agreements",
    admin_video_center: "Video Center",
    admin_vendor_analytics: "Vendor Analytics",
    admin_vendors: "Vendors",
    admin_customers: "Customers",
    admin_bookings: "Bookings",
    admin_blog: "Blog",
    admin_social_media: "Social Media",
    admin_notifications: "Notifications",
    admin_settings: "Settings",
    
    // Admin Stats
    admin_total_customers: "Total Customers",
    admin_total_vendors: "Total Vendors",
    admin_pending: "pending",
    admin_total_bookings: "Total Bookings",
    admin_monthly_revenue: "Monthly Revenue",
    admin_active_bookings: "Active Bookings",
    admin_completed_today: "Completed Today",
    admin_pending_approval: "Pending Approval",
    admin_cancelled: "Cancelled",
    
    // Admin Actions
    admin_view_all: "View All",
    admin_approve: "Approve",
    admin_reject: "Reject",
    admin_details: "Details",
    admin_edit: "Edit",
    admin_delete: "Delete",
    admin_save: "Save",
    admin_cancel: "Cancel",
    admin_search: "Search",
    admin_filter: "Filter",
    admin_export: "Export",
    admin_refresh: "Refresh",
    
    // Admin Sections
    admin_quick_actions: "Quick Actions",
    admin_recent_activity: "Recent Activity",
    admin_pending_vendors: "Pending Vendors",
    admin_recent_bookings: "Recent Bookings",
    admin_platform_settings: "Platform Settings",
    admin_platform_name: "Platform Name",
    admin_support_email: "Support Email",
    admin_platform_fee: "Platform Fee",
    admin_vip_membership: "VIP Membership Price",
    admin_changes_saved: "Changes saved successfully",
    admin_save_changes: "Save Changes",
    admin_cancel_changes: "Cancel Changes",
    admin_unsaved_changes: "You have unsaved changes",
    
    // Video Center
    admin_video_gallery: "Video Gallery",
    admin_video_downloads: "Video Downloads",
    admin_generate_video: "Generate Video",
    admin_video_status: "Video Status",
    admin_generating: "Generating...",
    admin_completed: "Completed",
    admin_failed: "Failed",
    
    // CRM
    admin_customer_management: "Customer Management",
    admin_vendor_management: "Vendor Management",
    admin_total_revenue: "Total Revenue",
    admin_average_order: "Average Order Value",
    admin_active_users: "Active Users",
    admin_vip_members: "VIP Members",
    admin_basic_tier: "Basic Tier",
    admin_pro_tier: "Pro Tier",
    admin_elite_tier: "Elite Tier",
    
    // Map
    admin_all_locations: "All Locations",
    admin_customers_only: "Customers Only",
    admin_vendors_only: "Vendors Only",
    admin_map_legend: "Map Legend",
    admin_basic_customer: "Basic Customer",
    admin_vip_customer: "VIP Customer",
    admin_basic_vendor: "Basic Vendor",
    admin_pro_vendor: "Pro Vendor",
    admin_elite_vendor: "Elite Vendor",
    admin_mobile_vendor: "Mobile Vendor",
    admin_shop_vendor: "Shop/Brick & Mortar",
    
    // Insurance
    admin_insurance_verification: "Insurance Verification",
    admin_verify_documents: "Verify Documents",
    admin_document_status: "Document Status",
    admin_verified: "Verified",
    admin_pending_review: "Pending Review",
    admin_expired: "Expired",
    
    // Notifications
    admin_notification_analytics: "Notification Analytics",
    admin_sent_notifications: "Sent Notifications",
    admin_delivery_rate: "Delivery Rate",
    admin_open_rate: "Open Rate",
    
    // Customer Dashboard
    dashboard_welcome: "Welcome back,",
    dashboard_home: "Home",
    dashboard_discover: "Discover",
    dashboard_rewards: "Rewards",
    dashboard_history: "History",
    dashboard_inbox: "Inbox",
    dashboard_profile: "Profile",
    dashboard_current_location: "Current Location",
    dashboard_what_needs_washing: "What needs washing?",
    dashboard_quick_book: "Quick Book",
    dashboard_book_now: "Book Now",
    dashboard_popular_services: "Popular Services",
    dashboard_featured_vendors: "Featured Vendors",
    dashboard_my_vehicles: "My Vehicles",
    dashboard_add_vehicle: "Add",
    dashboard_no_vehicles: "No vehicles added yet",
    dashboard_add_first_vehicle: "Add Your First Vehicle",
    dashboard_find_vendors: "Find Vendors",
    dashboard_see_all: "See all",
    dashboard_see_how_works: "See How It Works",
    dashboard_earn_referral: "Earn $10 Per Referral",
    dashboard_invite_friends: "Invite Friends & Earn",
    dashboard_messages: "Messages",
    dashboard_logout: "Logout",
    dashboard_profile_settings: "Profile Settings",
    dashboard_favorites: "Favorites",
    
    // Vehicle Types
    vehicle_car: "Car",
    vehicle_boat: "Boat",
    vehicle_rv: "RV",
    vehicle_fleet: "Fleet",
    vehicle_golf_cart: "Golf Cart",
    vehicle_recreational: "Recreational",
    vehicle_luxury: "Luxury",
    
    // Booking Flow
    booking_select_date: "Select Date",
    booking_select_time: "Select Time",
    booking_location: "Service Location",
    booking_use_my_location: "Use My Current Location",
    booking_search_address: "Search Address",
    booking_special_instructions: "Special Instructions",
    booking_select_vehicle: "Select Vehicle",
    booking_choose_package: "Choose Your Package",
    booking_add_extras: "Add Extra Services",
    booking_review_order: "Review & Checkout",
    booking_payment: "Payment",
    booking_finding_vendor: "Finding your WashPro...",
    booking_vendor_assigned: "Vendor Assigned",
    booking_continue: "Continue",
    booking_back: "Back",
    booking_confirm_booking: "Confirm Booking",
    booking_pay_now: "Pay Now",
    booking_subtotal: "Subtotal",
    booking_service_fee: "Service Fee",
    booking_tip: "Tip",
    booking_total: "Total",
    booking_add_tip: "Add a Tip",
    booking_no_tip: "No Tip",
    booking_custom: "Custom",
    
    // Service Packages
    package_basic: "Basic Wash",
    package_deluxe: "Deluxe Wash",
    package_premium: "Premium Detail",
    package_duration: "min",
    
    // New Booking Flow Translations
    booking_premium_mobile: "Premium Mobile Detailing",
    booking_on_demand: "On-Demand Washing",
    booking_on_demand_desc: "Professional wash whenever you need it",
    booking_we_come: "We Come To You",
    booking_we_come_desc: "Service at your home, office, or anywhere",
    booking_trusted: "Trusted & Insured",
    booking_trusted_desc: "Verified professionals you can count on",
    booking_get_started: "Get Started",
    booking_skip: "Skip",
    booking_select_vehicle_type: "Select your vehicle type below",
    booking_car_wash: "Car Wash",
    booking_car_types: "Cars • SUVs • Trucks • Vans",
    booking_boat_detail: "Boat Detail",
    booking_boat_types: "Yachts • Pontoons • Jet Skis",
    booking_rv_wash: "RV Wash",
    booking_rv_types: "Motorhomes • Campers • Trailers",
    booking_insured_vendors: "Insured Vendors",
    booking_5_star: "5-Star Service",
    booking_same_day: "Same Day Available",
    booking_book_a_wash: "Book a Wash",
    booking_confirm_location: "Confirm Location",
    booking_where_vehicle: "Where is the vehicle located?",
    booking_current_location: "We'll use your current location",
    booking_use_current: "Use Current Location",
    booking_or: "OR",
    booking_enter_address: "Enter Address Manually",
    booking_address_placeholder: "Enter street address, city, state...",
    booking_location_confirmed: "Location Confirmed",
    booking_choose_service: "Choose Your Service",
    booking_from_quick: "From quick washes to full detailing",
    booking_most_popular: "Most Popular",
    booking_best_value: "Best Value",
    booking_premium: "Premium",
    booking_estimated_time: "Estimated",
    booking_select_vehicle_title: "Select Your Vehicle",
    booking_add_vehicle: "Add Your Vehicle",
    booking_no_vehicles: "No vehicles yet",
    booking_add_first_vehicle: "Add your first vehicle to get started",
    booking_vehicle_make: "Make",
    booking_vehicle_model: "Model",
    booking_vehicle_year: "Year",
    booking_vehicle_color: "Color",
    booking_save_vehicle: "Save Vehicle",
    booking_schedule_title: "Schedule Your Wash",
    booking_select_preferred: "Select your preferred date and time",
    booking_morning: "Morning",
    booking_afternoon: "Afternoon",
    booking_evening: "Evening",
    booking_checkout_title: "Checkout",
    booking_review_confirm: "Review and confirm your booking",
    booking_order_summary: "Order Summary",
    booking_promo_code: "Have a promo code?",
    booking_apply: "Apply",
    booking_vip_save: "VIP members save 10%",
    booking_join_vip: "Join VIP",
    booking_pay_with_card: "Pay with Card",
    booking_processing: "Processing Payment...",
    booking_finding_washer: "Finding Your WashPro",
    booking_matching: "We're matching you with the best rated washer in your area",
    booking_booking_confirmed: "Booking Confirmed!",
    booking_washer_assigned: "Your WashPro is on the way",
    booking_eta: "Estimated Arrival",
    booking_contact_washer: "Contact Washer",
    booking_track_location: "Track Location",
    booking_vip_exclusive: "VIP Exclusive Offer",
    booking_vip_save_10: "Save 10% on every wash",
    booking_vip_month: "/month",
    booking_vip_priority: "Priority booking",
    booking_vip_support: "24/7 premium support",
    booking_vip_cancel: "Cancel anytime",
    booking_become_vip: "Become VIP Member",
    booking_no_thanks: "No Thanks",
    
    // Vendor Portal
    vendor_dashboard: "Dashboard",
    vendor_jobs: "Jobs",
    vendor_pricing: "Pricing",
    vendor_rankings: "Rankings",
    vendor_insurance: "Insurance",
    vendor_earnings: "Earnings",
    vendor_settings: "Settings",
    vendor_available: "Available for Jobs",
    vendor_unavailable: "Unavailable",
    vendor_new_job: "New Job Request",
    vendor_accept: "Accept",
    vendor_decline: "Decline",
    vendor_in_progress: "In Progress",
    vendor_completed: "Completed",
    vendor_total_earnings: "Total Earnings",
    vendor_this_week: "This Week",
    vendor_this_month: "This Month",
    
    // Common Actions
    action_save: "Save",
    action_cancel: "Cancel",
    action_edit: "Edit",
    action_delete: "Delete",
    action_confirm: "Confirm",
    action_close: "Close",
    action_loading: "Loading...",
    action_submitting: "Submitting...",
    action_success: "Success!",
    action_error: "Error",
    action_retry: "Retry",
  },
  
  es: {
    // Header
    nav_services: "Servicios",
    nav_how_it_works: "Cómo Funciona",
    nav_vendors: "Para Vendedores",
    nav_pricing: "Precios",
    nav_login: "Iniciar Sesión",
    nav_get_started: "Comenzar",
    
    // Hero Section
    hero_tagline: "SEDE DE CERA Y BRILLO",
    hero_subtitle: "LA EMPRESA DE CONSERJERÍA DE LAVADO DE AUTOS",
    hero_title_1: "Lavado de Autos",
    hero_title_2: "Móvil Premium",
    hero_title_3: "Bajo Demanda",
    hero_description: "Experimenta el futuro del cuidado automotriz. Servicios de detallado profesional entregados en tu puerta por vendedores verificados y asegurados.",
    hero_cta: "Reserva Tu Lavado",
    hero_watch_demo: "Ver Demo",
    hero_feature_1: "Vendedores 5 Estrellas",
    hero_feature_2: "Asegurados y Verificados",
    hero_feature_3: "Servicio Bajo Demanda",
    
    // Services Section
    services_title: "Nuestros Servicios",
    services_subtitle: "Soluciones premium de cuidado automotriz para cada tipo de vehículo",
    service_car_wash: "Lavado de Auto Premium",
    service_car_wash_desc: "Detallado móvil profesional en tu puerta",
    service_boat: "Detallado de Barcos",
    service_boat_desc: "Limpieza de grado marino para todas las embarcaciones",
    service_rv: "Lavado de RV",
    service_rv_desc: "Servicio completo de limpieza de RV y autocaravanas",
    service_fleet: "Gestión de Flotas",
    service_fleet_desc: "Soluciones de lavado para flotas comerciales",
    
    // How It Works
    how_it_works_title: "Cómo Funciona",
    how_it_works_subtitle: "Obtén tu vehículo reluciente en 3 simples pasos",
    step_1_title: "Reserva en Línea",
    step_1_desc: "Elige tu servicio, vehículo y horario preferido",
    step_2_title: "Vamos a Ti",
    step_2_desc: "Nuestro vendedor verificado llega a tu ubicación",
    step_3_title: "Disfruta el Brillo",
    step_3_desc: "Relájate mientras transformamos tu vehículo",
    
    // For Vendors
    vendors_title: "Para Vendedores",
    vendors_subtitle: "Únete a nuestra red de detalladores profesionales",
    vendors_benefit_1: "Establece Tu Propio Horario",
    vendors_benefit_1_desc: "Trabaja cuando quieras, donde quieras",
    vendors_benefit_2: "Haz Crecer Tu Negocio",
    vendors_benefit_2_desc: "Accede a miles de clientes en tu área",
    vendors_benefit_3: "Cobra Rápido",
    vendors_benefit_3_desc: "Pagos semanales directamente a tu cuenta",
    vendors_cta: "Conviértete en Vendedor",
    vendors_pricing: "Ver Precios de Servicios",
    vendors_watch_tutorial: "Ver Video Tutorial",
    
    // Pricing Section
    pricing_title: "Guía de Precios de Servicios",
    pricing_subtitle: "Precios iniciales para todos los tipos de vehículos. Los vendedores establecen sus propias tarifas.",
    pricing_car: "Auto / Sedán",
    pricing_car_types: "Cupés, Sedanes, Hatchbacks",
    pricing_suv: "SUV / Camioneta",
    pricing_suv_types: "SUVs, Camionetas, Vans",
    pricing_boat: "Barcos",
    pricing_boat_types: "Todos los tipos de embarcaciones",
    pricing_rv: "RVs",
    pricing_rv_types: "Autocaravanas y Campers",
    pricing_starting: "Desde",
    pricing_per_ft: "/pie sobre",
    
    // Add-ons
    addons_title: "Servicios Adicionales Populares",
    addon_vacuum: "Aspirado Interior",
    addon_leather: "Cuidado de Cuero",
    addon_wax: "Sellador de Cera",
    addon_pet_hair: "Remoción de Pelo de Mascota",
    pricing_disclaimer: "*Precios iniciales mostrados. Los vendedores establecen sus propias tarifas. Se aplica una tarifa de servicio de red del 20%.",
    
    // Vendor Pricing Note
    vendor_pricing_title: "Los Vendedores Establecen Sus Propios Precios",
    vendor_pricing_desc: "Cada vendedor puede personalizar sus precios. Compara vendedores para encontrar las mejores tarifas en tu área.",
    vendor_pricing_tip: "Consejo: Precios competitivos atraen más clientes - si los precios son muy altos, los clientes pueden elegir una opción más económica.",
    vendor_pricing_cta: "Panel de Precios del Vendedor",
    
    // Booking Policy
    policy_title: "Política de Reservas y Cancelaciones",
    policy_arrival_title: "Expectativas de Llegada del Vendedor",
    policy_arrival_1: "Los vendedores deben llegar dentro de 30 minutos de la hora programada",
    policy_arrival_2: "Si se retrasan, los vendedores deben proporcionar actualizaciones constantes al cliente",
    policy_arrival_3: "Seguimiento en tiempo real disponible una vez que el vendedor está en camino",
    policy_cancel_title: "Política de Cancelación del Cliente",
    policy_cancel_free: "Cancelación gratuita antes de que el vendedor acepte",
    policy_cancel_fee: "Tarifa de cancelación del 20% si se cancela después de que el vendedor está en camino o dentro de 20 minutos",
    policy_cancel_brick: "La misma política aplica para citas en tiendas físicas",
    policy_why: "¿Por qué esta política?",
    policy_why_desc: "Valoramos el tiempo de vendedores y clientes. Esta política protege a los vendedores de viajes desperdiciados.",
    
    // Membership
    membership_title: "Membresía de Cliente",
    membership_subtitle: "Únete gratis o mejora a VIP para beneficios extra",
    membership_free: "Gratis",
    membership_free_desc: "Perfecto para lavados ocasionales",
    membership_vip: "Miembro VIP",
    membership_vip_desc: "Para entusiastas del cuidado automotriz",
    membership_feature_booking: "Reserva en línea fácil",
    membership_feature_vendors: "Acceso a todos los vendedores",
    membership_feature_tracking: "Seguimiento en tiempo real",
    membership_feature_support: "Soporte al cliente",
    membership_feature_priority: "Reserva prioritaria",
    membership_feature_discount: "10% de descuento en todos los servicios",
    membership_feature_rewards: "Recompensas exclusivas",
    membership_feature_deals: "Acceso anticipado a ofertas",
    membership_cta_free: "Comienza Gratis",
    membership_cta_vip: "Únete a VIP",
    
    // Footer
    footer_pay_per_wash: "Paga solo cuando laves",
    footer_secure: "Pagos seguros",
    footer_platform_fee: "Tarifa de plataforma:",
    footer_vip_video: "Ver Beneficios VIP en Acción",
    
    // Portal Selection
    portal_title: "ELIGE TU PORTAL",
    portal_customer: "Cliente",
    portal_vendor: "Vendedor",
    portal_admin: "Administrador",
    
    // Common
    learn_more: "Más Información",
    contact_us: "Contáctanos",
    terms: "Términos de Servicio",
    privacy: "Política de Privacidad",
    per_month: "/mes",
    recommended: "Recomendado",
    
    // Service History & Recurring
    service_history: "Historial de Servicios",
    past_bookings: "Tus Reservas Anteriores",
    recurring_bookings: "Reservas Recurrentes",
    no_bookings: "Sin reservas aún",
    rebook_service: "Reservar Este Servicio Nuevamente",
    make_recurring: "Hacer Recurrente",
    add_tip: "Agregar Propina",
    tip_added: "Propina Agregada",
    every_x_days: "Cada {days} días",
    pause: "Pausar",
    resume: "Reanudar",
    cancel_recurring: "Cancelar Recurrente",
    
    // Tip Collection
    add_a_tip: "Agregar Propina",
    show_appreciation: "Muestra Tu Agradecimiento",
    tip_goes_to_vendor: "100% de tu propina va directamente a",
    send_tip: "Enviar Propina",
    skip_for_now: "Omitir por ahora",
    thank_you: "¡Gracias!",
    tip_sent: "Tu propina ha sido enviada",
    
    // Fleet Contact
    fleet_management: "Gestión de Flotas",
    get_custom_pricing: "Obtén precios personalizados para tu negocio",
    company_info: "Información de la Empresa",
    company_name: "Nombre de la Empresa",
    contact_name: "Nombre de Contacto",
    fleet_details: "Detalles de la Flota",
    vehicle_types: "Tipos de Vehículos en la Flota",
    fleet_size: "Tamaño de la Flota",
    service_frequency: "Frecuencia de Servicio Deseada",
    submit_inquiry: "Enviar Consulta de Flota",
    inquiry_submitted: "¡Consulta Enviada!",
    
    // Shop Finder
    find_shop: "Encontrar una Tienda",
    nearby_shops: "Tiendas de Lavado Cercanas",
    book_appointment: "Reservar Cita",
    select_date: "Seleccionar Fecha",
    select_time: "Seleccionar Hora",
    available_slots: "Horarios Disponibles",
    confirm_booking: "Confirmar Reserva",
    
    // Admin Dashboard
    admin_dashboard: "Panel de Administración",
    admin_master_access: "Acceso de Administrador Maestro",
    admin_full_control: "Control total sobre la plataforma WashPros",
    admin_service_map: "Mapa de Servicios",
    admin_crm: "CRM",
    admin_revenue: "Ingresos",
    admin_pricing: "Precios",
    admin_surge_config: "Configuración de Demanda",
    admin_surge_analytics: "Análisis de Demanda",
    admin_leaderboard: "Tabla de Líderes",
    admin_insurance: "Seguros",
    admin_agreements: "Acuerdos",
    admin_video_center: "Centro de Videos",
    admin_vendor_analytics: "Análisis de Vendedores",
    admin_vendors: "Vendedores",
    admin_customers: "Clientes",
    admin_bookings: "Reservas",
    admin_blog: "Blog",
    admin_social_media: "Redes Sociales",
    admin_notifications: "Notificaciones",
    admin_settings: "Configuración",
    
    // Admin Stats
    admin_total_customers: "Total de Clientes",
    admin_total_vendors: "Total de Vendedores",
    admin_pending: "pendiente",
    admin_total_bookings: "Total de Reservas",
    admin_monthly_revenue: "Ingresos Mensuales",
    admin_active_bookings: "Reservas Activas",
    admin_completed_today: "Completadas Hoy",
    admin_pending_approval: "Pendientes de Aprobación",
    admin_cancelled: "Canceladas",
    
    // Admin Actions
    admin_view_all: "Ver Todo",
    admin_approve: "Aprobar",
    admin_reject: "Rechazar",
    admin_details: "Detalles",
    admin_edit: "Editar",
    admin_delete: "Eliminar",
    admin_save: "Guardar",
    admin_cancel: "Cancelar",
    admin_search: "Buscar",
    admin_filter: "Filtrar",
    admin_export: "Exportar",
    admin_refresh: "Actualizar",
    
    // Admin Sections
    admin_quick_actions: "Acciones Rápidas",
    admin_recent_activity: "Actividad Reciente",
    admin_pending_vendors: "Vendedores Pendientes",
    admin_recent_bookings: "Reservas Recientes",
    admin_platform_settings: "Configuración de Plataforma",
    admin_platform_name: "Nombre de Plataforma",
    admin_support_email: "Email de Soporte",
    admin_platform_fee: "Tarifa de Plataforma",
    admin_vip_membership: "Precio de Membresía VIP",
    admin_changes_saved: "Cambios guardados exitosamente",
    admin_save_changes: "Guardar Cambios",
    admin_cancel_changes: "Cancelar Cambios",
    admin_unsaved_changes: "Tienes cambios sin guardar",
    
    // Video Center
    admin_video_gallery: "Galería de Videos",
    admin_video_downloads: "Descargas de Videos",
    admin_generate_video: "Generar Video",
    admin_video_status: "Estado del Video",
    admin_generating: "Generando...",
    admin_completed: "Completado",
    admin_failed: "Fallido",
    
    // CRM
    admin_customer_management: "Gestión de Clientes",
    admin_vendor_management: "Gestión de Vendedores",
    admin_total_revenue: "Ingresos Totales",
    admin_average_order: "Valor Promedio de Orden",
    admin_active_users: "Usuarios Activos",
    admin_vip_members: "Miembros VIP",
    admin_basic_tier: "Nivel Básico",
    admin_pro_tier: "Nivel Pro",
    admin_elite_tier: "Nivel Elite",
    
    // Map
    admin_all_locations: "Todas las Ubicaciones",
    admin_customers_only: "Solo Clientes",
    admin_vendors_only: "Solo Vendedores",
    admin_map_legend: "Leyenda del Mapa",
    admin_basic_customer: "Cliente Básico",
    admin_vip_customer: "Cliente VIP",
    admin_basic_vendor: "Vendedor Básico",
    admin_pro_vendor: "Vendedor Pro",
    admin_elite_vendor: "Vendedor Elite",
    admin_mobile_vendor: "Vendedor Móvil",
    admin_shop_vendor: "Tienda/Local",
    
    // Insurance
    admin_insurance_verification: "Verificación de Seguros",
    admin_verify_documents: "Verificar Documentos",
    admin_document_status: "Estado del Documento",
    admin_verified: "Verificado",
    admin_pending_review: "Revisión Pendiente",
    admin_expired: "Vencido",
    
    // Notifications
    admin_notification_analytics: "Análisis de Notificaciones",
    admin_sent_notifications: "Notificaciones Enviadas",
    admin_delivery_rate: "Tasa de Entrega",
    admin_open_rate: "Tasa de Apertura",
    
    // Customer Dashboard
    dashboard_welcome: "Bienvenido de nuevo,",
    dashboard_home: "Inicio",
    dashboard_discover: "Descubrir",
    dashboard_rewards: "Recompensas",
    dashboard_history: "Historial",
    dashboard_inbox: "Bandeja",
    dashboard_profile: "Perfil",
    dashboard_current_location: "Ubicación Actual",
    dashboard_what_needs_washing: "¿Qué necesita lavado?",
    dashboard_quick_book: "Reserva Rápida",
    dashboard_book_now: "Reservar Ahora",
    dashboard_popular_services: "Servicios Populares",
    dashboard_featured_vendors: "Vendedores Destacados",
    dashboard_my_vehicles: "Mis Vehículos",
    dashboard_add_vehicle: "Agregar",
    dashboard_no_vehicles: "No hay vehículos agregados",
    dashboard_add_first_vehicle: "Agrega Tu Primer Vehículo",
    dashboard_find_vendors: "Encontrar Vendedores",
    dashboard_see_all: "Ver todo",
    dashboard_see_how_works: "Ver Cómo Funciona",
    dashboard_earn_referral: "Gana $10 Por Referido",
    dashboard_invite_friends: "Invita Amigos y Gana",
    dashboard_messages: "Mensajes",
    dashboard_logout: "Cerrar Sesión",
    dashboard_profile_settings: "Configuración de Perfil",
    dashboard_favorites: "Favoritos",
    
    // Vehicle Types
    vehicle_car: "Auto",
    vehicle_boat: "Barco",
    vehicle_rv: "RV",
    vehicle_fleet: "Flota",
    vehicle_golf_cart: "Carrito de Golf",
    vehicle_recreational: "Recreativo",
    
    // Booking Flow
    booking_select_date: "Seleccionar Fecha",
    booking_select_time: "Seleccionar Hora",
    booking_location: "Ubicación del Servicio",
    booking_use_my_location: "Usar Mi Ubicación Actual",
    booking_search_address: "Buscar Dirección",
    booking_special_instructions: "Instrucciones Especiales",
    booking_select_vehicle: "Seleccionar Vehículo",
    booking_choose_package: "Elige Tu Paquete",
    booking_add_extras: "Agregar Servicios Extra",
    booking_review_order: "Revisar y Pagar",
    booking_payment: "Pago",
    booking_finding_vendor: "Buscando tu WashPro...",
    booking_vendor_assigned: "Vendedor Asignado",
    booking_continue: "Continuar",
    booking_back: "Atrás",
    booking_confirm_booking: "Confirmar Reserva",
    booking_pay_now: "Pagar Ahora",
    booking_subtotal: "Subtotal",
    booking_service_fee: "Tarifa de Servicio",
    booking_tip: "Propina",
    booking_total: "Total",
    booking_add_tip: "Agregar Propina",
    booking_no_tip: "Sin Propina",
    booking_custom: "Personalizado",
    
    // Service Packages
    package_basic: "Lavado Básico",
    package_deluxe: "Lavado Deluxe",
    package_premium: "Detalle Premium",
    package_duration: "min",
    
    // New Booking Flow Translations
    booking_premium_mobile: "Detallado Móvil Premium",
    booking_on_demand: "Lavado Bajo Demanda",
    booking_on_demand_desc: "Lavado profesional cuando lo necesites",
    booking_we_come: "Vamos a Ti",
    booking_we_come_desc: "Servicio en tu casa, oficina o donde sea",
    booking_trusted: "Confiable y Asegurado",
    booking_trusted_desc: "Profesionales verificados en los que puedes confiar",
    booking_get_started: "Comenzar",
    booking_skip: "Omitir",
    booking_select_vehicle_type: "Selecciona tu tipo de vehículo",
    booking_car_wash: "Lavado de Auto",
    booking_car_types: "Autos • SUVs • Camionetas • Vans",
    booking_boat_detail: "Detalle de Barco",
    booking_boat_types: "Yates • Pontones • Motos Acuáticas",
    booking_rv_wash: "Lavado de RV",
    booking_rv_types: "Autocaravanas • Campers • Remolques",
    booking_insured_vendors: "Vendedores Asegurados",
    booking_5_star: "Servicio 5 Estrellas",
    booking_same_day: "Disponible Hoy",
    booking_book_a_wash: "Reservar Lavado",
    booking_confirm_location: "Confirmar Ubicación",
    booking_where_vehicle: "¿Dónde está el vehículo?",
    booking_current_location: "Usaremos tu ubicación actual",
    booking_use_current: "Usar Ubicación Actual",
    booking_or: "O",
    booking_enter_address: "Ingresar Dirección Manualmente",
    booking_address_placeholder: "Ingresa dirección, ciudad, estado...",
    booking_location_confirmed: "Ubicación Confirmada",
    booking_choose_service: "Elige Tu Servicio",
    booking_from_quick: "Desde lavados rápidos hasta detallado completo",
    booking_most_popular: "Más Popular",
    booking_best_value: "Mejor Valor",
    booking_premium: "Premium",
    booking_estimated_time: "Estimado",
    booking_select_vehicle_title: "Selecciona Tu Vehículo",
    booking_add_vehicle: "Agregar Tu Vehículo",
    booking_no_vehicles: "Sin vehículos aún",
    booking_add_first_vehicle: "Agrega tu primer vehículo para comenzar",
    booking_vehicle_make: "Marca",
    booking_vehicle_model: "Modelo",
    booking_vehicle_year: "Año",
    booking_vehicle_color: "Color",
    booking_save_vehicle: "Guardar Vehículo",
    booking_schedule_title: "Programa Tu Lavado",
    booking_select_preferred: "Selecciona tu fecha y hora preferida",
    booking_morning: "Mañana",
    booking_afternoon: "Tarde",
    booking_evening: "Noche",
    booking_checkout_title: "Pago",
    booking_review_confirm: "Revisa y confirma tu reserva",
    booking_order_summary: "Resumen del Pedido",
    booking_promo_code: "¿Tienes código promocional?",
    booking_apply: "Aplicar",
    booking_vip_save: "Miembros VIP ahorran 10%",
    booking_join_vip: "Unirse VIP",
    booking_pay_with_card: "Pagar con Tarjeta",
    booking_processing: "Procesando Pago...",
    booking_finding_washer: "Buscando Tu WashPro",
    booking_matching: "Te estamos emparejando con el mejor lavador de tu área",
    booking_booking_confirmed: "¡Reserva Confirmada!",
    booking_washer_assigned: "Tu WashPro está en camino",
    booking_eta: "Llegada Estimada",
    booking_contact_washer: "Contactar Lavador",
    booking_track_location: "Rastrear Ubicación",
    booking_vip_exclusive: "Oferta Exclusiva VIP",
    booking_vip_save_10: "Ahorra 10% en cada lavado",
    booking_vip_month: "/mes",
    booking_vip_priority: "Reserva prioritaria",
    booking_vip_support: "Soporte premium 24/7",
    booking_vip_cancel: "Cancela cuando quieras",
    booking_become_vip: "Hacerse Miembro VIP",
    booking_no_thanks: "No Gracias",
    
    // Vendor Portal
    vendor_dashboard: "Panel",
    vendor_jobs: "Trabajos",
    vendor_pricing: "Precios",
    vendor_rankings: "Clasificación",
    vendor_insurance: "Seguro",
    vendor_earnings: "Ganancias",
    vendor_settings: "Configuración",
    vendor_available: "Disponible para Trabajos",
    vendor_unavailable: "No Disponible",
    vendor_new_job: "Nueva Solicitud de Trabajo",
    vendor_accept: "Aceptar",
    vendor_decline: "Rechazar",
    vendor_in_progress: "En Progreso",
    vendor_completed: "Completado",
    vendor_total_earnings: "Ganancias Totales",
    vendor_this_week: "Esta Semana",
    vendor_this_month: "Este Mes",
    
    // Common Actions
    action_save: "Guardar",
    action_cancel: "Cancelar",
    action_edit: "Editar",
    action_delete: "Eliminar",
    action_confirm: "Confirmar",
    action_close: "Cerrar",
    action_loading: "Cargando...",
    action_submitting: "Enviando...",
    action_success: "¡Éxito!",
    action_error: "Error",
    action_retry: "Reintentar",
  },
  
  fr: {
    // Header
    nav_services: "Services",
    nav_how_it_works: "Comment Ça Marche",
    nav_vendors: "Pour les Vendeurs",
    nav_pricing: "Tarifs",
    nav_login: "Connexion",
    nav_get_started: "Commencer",
    
    // Hero Section
    hero_tagline: "SIÈGE DE LA CIRE ET DE LA BRILLANCE",
    hero_subtitle: "LA SOCIÉTÉ DE CONCIERGERIE DE LAVAGE AUTO",
    hero_title_1: "Lavage Auto",
    hero_title_2: "Mobile Premium",
    hero_title_3: "À la Demande",
    hero_description: "Découvrez le futur de l'entretien automobile. Services de détailing professionnels livrés à votre porte par des vendeurs vérifiés et assurés.",
    hero_cta: "Réservez Votre Lavage",
    hero_watch_demo: "Voir la Démo",
    hero_feature_1: "Vendeurs 5 Étoiles",
    hero_feature_2: "Assurés et Vérifiés",
    hero_feature_3: "Service à la Demande",
    
    // Services Section
    services_title: "Nos Services",
    services_subtitle: "Solutions premium d'entretien auto pour chaque type de véhicule",
    service_car_wash: "Lavage Auto Premium",
    service_car_wash_desc: "Détailing mobile professionnel à votre porte",
    service_boat: "Détailing Bateau",
    service_boat_desc: "Nettoyage de qualité marine pour toutes les embarcations",
    service_rv: "Lavage Camping-Car",
    service_rv_desc: "Service complet de nettoyage camping-car",
    service_fleet: "Gestion de Flotte",
    service_fleet_desc: "Solutions de lavage pour flottes commerciales",
    
    // How It Works
    how_it_works_title: "Comment Ça Marche",
    how_it_works_subtitle: "Obtenez votre véhicule étincelant en 3 étapes simples",
    step_1_title: "Réservez en Ligne",
    step_1_desc: "Choisissez votre service, véhicule et créneau horaire",
    step_2_title: "Nous Venons à Vous",
    step_2_desc: "Notre vendeur vérifié arrive à votre emplacement",
    step_3_title: "Profitez de la Brillance",
    step_3_desc: "Détendez-vous pendant que nous transformons votre véhicule",
    
    // For Vendors
    vendors_title: "Pour les Vendeurs",
    vendors_subtitle: "Rejoignez notre réseau de détailleurs professionnels",
    vendors_benefit_1: "Définissez Votre Propre Horaire",
    vendors_benefit_1_desc: "Travaillez quand vous voulez, où vous voulez",
    vendors_benefit_2: "Développez Votre Entreprise",
    vendors_benefit_2_desc: "Accédez à des milliers de clients dans votre région",
    vendors_benefit_3: "Soyez Payé Rapidement",
    vendors_benefit_3_desc: "Paiements hebdomadaires directement sur votre compte",
    vendors_cta: "Devenez Vendeur",
    vendors_pricing: "Voir les Tarifs des Services",
    vendors_watch_tutorial: "Voir le Tutoriel Vidéo",
    
    // Pricing Section
    pricing_title: "Guide des Tarifs des Services",
    pricing_subtitle: "Prix de départ pour tous les types de véhicules. Les vendeurs fixent leurs propres tarifs.",
    pricing_car: "Voiture / Berline",
    pricing_car_types: "Coupés, Berlines, Compactes",
    pricing_suv: "SUV / Camion",
    pricing_suv_types: "SUVs, Camions, Fourgons",
    pricing_boat: "Bateaux",
    pricing_boat_types: "Tous types d'embarcations",
    pricing_rv: "Camping-Cars",
    pricing_rv_types: "Autocaravanes et Campeurs",
    pricing_starting: "À partir de",
    pricing_per_ft: "/pied au-delà de",
    
    // Add-ons
    addons_title: "Services Supplémentaires Populaires",
    addon_vacuum: "Aspiration Intérieure",
    addon_leather: "Soin du Cuir",
    addon_wax: "Scellant à Cire",
    addon_pet_hair: "Enlèvement Poils d'Animaux",
    pricing_disclaimer: "*Prix de départ affichés. Les vendeurs fixent leurs propres tarifs. Des frais de service réseau de 20% s'appliquent.",
    
    // Vendor Pricing Note
    vendor_pricing_title: "Les Vendeurs Fixent Leurs Propres Prix",
    vendor_pricing_desc: "Chaque vendeur peut personnaliser ses tarifs. Comparez les vendeurs pour trouver les meilleurs prix.",
    vendor_pricing_tip: "Conseil : Des prix compétitifs attirent plus de clients - si les prix sont trop élevés, les clients peuvent choisir une option moins chère.",
    vendor_pricing_cta: "Tableau de Bord des Prix Vendeur",
    
    // Booking Policy
    policy_title: "Politique de Réservation et d'Annulation",
    policy_arrival_title: "Attentes d'Arrivée du Vendeur",
    policy_arrival_1: "Les vendeurs doivent arriver dans les 30 minutes de l'heure prévue",
    policy_arrival_2: "En cas de retard, les vendeurs doivent fournir des mises à jour constantes au client",
    policy_arrival_3: "Suivi en temps réel disponible une fois le vendeur en route",
    policy_cancel_title: "Politique d'Annulation Client",
    policy_cancel_free: "Annulation gratuite avant l'acceptation du vendeur",
    policy_cancel_fee: "Frais d'annulation de 20% si annulé après que le vendeur est en route ou dans les 20 minutes",
    policy_cancel_brick: "La même politique s'applique aux rendez-vous en magasin",
    policy_why: "Pourquoi cette politique ?",
    policy_why_desc: "Nous valorisons le temps des vendeurs et des clients. Cette politique protège les vendeurs des déplacements inutiles.",
    
    // Membership
    membership_title: "Adhésion Client",
    membership_subtitle: "Inscrivez-vous gratuitement ou passez au VIP pour des avantages supplémentaires",
    membership_free: "Gratuit",
    membership_free_desc: "Parfait pour les lavages occasionnels",
    membership_vip: "Membre VIP",
    membership_vip_desc: "Pour les passionnés d'entretien auto",
    membership_feature_booking: "Réservation en ligne facile",
    membership_feature_vendors: "Accès à tous les vendeurs",
    membership_feature_tracking: "Suivi en temps réel",
    membership_feature_support: "Support client",
    membership_feature_priority: "Réservation prioritaire",
    membership_feature_discount: "10% de réduction sur tous les services",
    membership_feature_rewards: "Récompenses exclusives",
    membership_feature_deals: "Accès anticipé aux offres",
    membership_cta_free: "Commencer Gratuitement",
    membership_cta_vip: "Rejoindre VIP",
    
    // Footer
    footer_pay_per_wash: "Payez uniquement quand vous lavez",
    footer_secure: "Paiements sécurisés",
    footer_platform_fee: "Frais de plateforme:",
    footer_vip_video: "Voir les Avantages VIP en Action",
    
    // Portal Selection
    portal_title: "CHOISISSEZ VOTRE PORTAIL",
    portal_customer: "Client",
    portal_vendor: "Vendeur",
    portal_admin: "Administrateur",
    
    // Common
    learn_more: "En Savoir Plus",
    contact_us: "Nous Contacter",
    terms: "Conditions d'Utilisation",
    privacy: "Politique de Confidentialité",
    per_month: "/mois",
    recommended: "Recommandé",
    
    // Admin Dashboard
    admin_dashboard: "Tableau de Bord Admin",
    admin_master_access: "Accès Administrateur Principal",
    admin_full_control: "Contrôle total sur la plateforme WashPros",
    admin_service_map: "Carte des Services",
    admin_crm: "CRM",
    admin_revenue: "Revenus",
    admin_pricing: "Tarification",
    admin_surge_config: "Config. Demande",
    admin_surge_analytics: "Analytique Demande",
    admin_leaderboard: "Classement",
    admin_insurance: "Assurances",
    admin_agreements: "Accords",
    admin_video_center: "Centre Vidéo",
    admin_vendor_analytics: "Analytique Vendeurs",
    admin_vendors: "Vendeurs",
    admin_customers: "Clients",
    admin_bookings: "Réservations",
    admin_blog: "Blog",
    admin_social_media: "Réseaux Sociaux",
    admin_notifications: "Notifications",
    admin_settings: "Paramètres",
    
    // Admin Stats
    admin_total_customers: "Total Clients",
    admin_total_vendors: "Total Vendeurs",
    admin_pending: "en attente",
    admin_total_bookings: "Total Réservations",
    admin_monthly_revenue: "Revenus Mensuels",
    admin_active_bookings: "Réservations Actives",
    admin_completed_today: "Complétées Aujourd'hui",
    admin_pending_approval: "En Attente d'Approbation",
    admin_cancelled: "Annulées",
    
    // Admin Actions
    admin_view_all: "Voir Tout",
    admin_approve: "Approuver",
    admin_reject: "Rejeter",
    admin_details: "Détails",
    admin_edit: "Modifier",
    admin_delete: "Supprimer",
    admin_save: "Enregistrer",
    admin_cancel: "Annuler",
    admin_search: "Rechercher",
    admin_filter: "Filtrer",
    admin_export: "Exporter",
    admin_refresh: "Actualiser",
    
    // Admin Sections
    admin_quick_actions: "Actions Rapides",
    admin_recent_activity: "Activité Récente",
    admin_pending_vendors: "Vendeurs en Attente",
    admin_recent_bookings: "Réservations Récentes",
    admin_platform_settings: "Paramètres Plateforme",
    admin_platform_name: "Nom de la Plateforme",
    admin_support_email: "Email Support",
    admin_platform_fee: "Frais de Plateforme",
    admin_vip_membership: "Prix Adhésion VIP",
    admin_changes_saved: "Modifications enregistrées",
    admin_save_changes: "Enregistrer",
    admin_cancel_changes: "Annuler",
    admin_unsaved_changes: "Modifications non enregistrées",
    
    // Video Center
    admin_video_gallery: "Galerie Vidéo",
    admin_video_downloads: "Téléchargements Vidéo",
    admin_generate_video: "Générer Vidéo",
    admin_video_status: "Statut Vidéo",
    admin_generating: "Génération...",
    admin_completed: "Terminé",
    admin_failed: "Échoué",
    
    // CRM
    admin_customer_management: "Gestion Clients",
    admin_vendor_management: "Gestion Vendeurs",
    admin_total_revenue: "Revenus Totaux",
    admin_average_order: "Valeur Moyenne Commande",
    admin_active_users: "Utilisateurs Actifs",
    admin_vip_members: "Membres VIP",
    admin_basic_tier: "Niveau Basic",
    admin_pro_tier: "Niveau Pro",
    admin_elite_tier: "Niveau Elite",
    
    // Map
    admin_all_locations: "Tous les Emplacements",
    admin_customers_only: "Clients Uniquement",
    admin_vendors_only: "Vendeurs Uniquement",
    admin_map_legend: "Légende",
    admin_basic_customer: "Client Basic",
    admin_vip_customer: "Client VIP",
    admin_basic_vendor: "Vendeur Basic",
    admin_pro_vendor: "Vendeur Pro",
    admin_elite_vendor: "Vendeur Elite",
    admin_mobile_vendor: "Vendeur Mobile",
    admin_shop_vendor: "Magasin/Local",
    
    // Insurance
    admin_insurance_verification: "Vérification Assurances",
    admin_verify_documents: "Vérifier Documents",
    admin_document_status: "Statut Document",
    admin_verified: "Vérifié",
    admin_pending_review: "En Cours de Révision",
    admin_expired: "Expiré",
    
    // Notifications
    admin_notification_analytics: "Analytique Notifications",
    admin_sent_notifications: "Notifications Envoyées",
    admin_delivery_rate: "Taux de Livraison",
    admin_open_rate: "Taux d'Ouverture",
    
    // Customer Dashboard
    dashboard_welcome: "Bon retour,",
    dashboard_home: "Accueil",
    dashboard_discover: "Découvrir",
    dashboard_rewards: "Récompenses",
    dashboard_history: "Historique",
    dashboard_inbox: "Boîte",
    dashboard_profile: "Profil",
    dashboard_current_location: "Position Actuelle",
    dashboard_what_needs_washing: "Que faut-il laver?",
    dashboard_quick_book: "Réservation Rapide",
    dashboard_book_now: "Réserver Maintenant",
    dashboard_popular_services: "Services Populaires",
    dashboard_featured_vendors: "Vendeurs en Vedette",
    dashboard_my_vehicles: "Mes Véhicules",
    dashboard_add_vehicle: "Ajouter",
    dashboard_no_vehicles: "Aucun véhicule ajouté",
    dashboard_add_first_vehicle: "Ajoutez Votre Premier Véhicule",
    dashboard_find_vendors: "Trouver Vendeurs",
    dashboard_see_all: "Voir tout",
    dashboard_see_how_works: "Voir Comment Ça Marche",
    dashboard_earn_referral: "Gagnez 10$ Par Parrainage",
    dashboard_invite_friends: "Invitez des Amis et Gagnez",
    dashboard_messages: "Messages",
    dashboard_logout: "Déconnexion",
    dashboard_profile_settings: "Paramètres du Profil",
    dashboard_favorites: "Favoris",
    
    // Vehicle Types
    vehicle_car: "Voiture",
    vehicle_boat: "Bateau",
    vehicle_rv: "Camping-Car",
    vehicle_fleet: "Flotte",
    vehicle_golf_cart: "Voiturette",
    vehicle_recreational: "Loisirs",
    
    // Booking Flow
    booking_select_date: "Sélectionner la Date",
    booking_select_time: "Sélectionner l'Heure",
    booking_location: "Lieu du Service",
    booking_use_my_location: "Utiliser Ma Position Actuelle",
    booking_search_address: "Rechercher une Adresse",
    booking_special_instructions: "Instructions Spéciales",
    booking_select_vehicle: "Sélectionner le Véhicule",
    booking_choose_package: "Choisissez Votre Forfait",
    booking_add_extras: "Ajouter des Services Supplémentaires",
    booking_review_order: "Vérifier et Payer",
    booking_payment: "Paiement",
    booking_finding_vendor: "Recherche de votre WashPro...",
    booking_vendor_assigned: "Vendeur Assigné",
    booking_continue: "Continuer",
    booking_back: "Retour",
    booking_confirm_booking: "Confirmer la Réservation",
    booking_pay_now: "Payer Maintenant",
    booking_subtotal: "Sous-total",
    booking_service_fee: "Frais de Service",
    booking_tip: "Pourboire",
    booking_total: "Total",
    booking_add_tip: "Ajouter un Pourboire",
    booking_no_tip: "Pas de Pourboire",
    booking_custom: "Personnalisé",
    
    // Service Packages
    package_basic: "Lavage Basique",
    package_deluxe: "Lavage Deluxe",
    package_premium: "Détail Premium",
    package_duration: "min",
    
    // Vendor Portal
    vendor_dashboard: "Tableau de Bord",
    vendor_jobs: "Travaux",
    vendor_pricing: "Tarification",
    vendor_rankings: "Classement",
    vendor_insurance: "Assurance",
    vendor_earnings: "Revenus",
    vendor_settings: "Paramètres",
    vendor_available: "Disponible pour Travaux",
    vendor_unavailable: "Non Disponible",
    vendor_new_job: "Nouvelle Demande de Travail",
    vendor_accept: "Accepter",
    vendor_decline: "Refuser",
    vendor_in_progress: "En Cours",
    vendor_completed: "Terminé",
    vendor_total_earnings: "Revenus Totaux",
    vendor_this_week: "Cette Semaine",
    vendor_this_month: "Ce Mois",
    
    // Common Actions
    action_save: "Enregistrer",
    action_cancel: "Annuler",
    action_edit: "Modifier",
    action_delete: "Supprimer",
    action_confirm: "Confirmer",
    action_close: "Fermer",
    action_loading: "Chargement...",
    action_submitting: "Envoi...",
    action_success: "Succès!",
    action_error: "Erreur",
    action_retry: "Réessayer",
  },
  
  pt: {
    // Header
    nav_services: "Serviços",
    nav_how_it_works: "Como Funciona",
    nav_vendors: "Para Vendedores",
    nav_pricing: "Preços",
    nav_login: "Entrar",
    nav_get_started: "Começar",
    
    // Hero Section
    hero_tagline: "SEDE DE CERA E BRILHO",
    hero_subtitle: "A EMPRESA DE CONCIERGE DE LAVAGEM DE CARROS",
    hero_title_1: "Lavagem de Carro",
    hero_title_2: "Móvel Premium",
    hero_title_3: "Sob Demanda",
    hero_description: "Experimente o futuro do cuidado automotivo. Serviços profissionais de detalhamento entregues na sua porta por vendedores verificados e segurados.",
    hero_cta: "Reserve Sua Lavagem",
    hero_watch_demo: "Ver Demo",
    hero_feature_1: "Vendedores 5 Estrelas",
    hero_feature_2: "Segurados e Verificados",
    hero_feature_3: "Serviço Sob Demanda",
    
    // Services Section
    services_title: "Nossos Serviços",
    services_subtitle: "Soluções premium de cuidado automotivo para cada tipo de veículo",
    service_car_wash: "Lavagem de Carro Premium",
    service_car_wash_desc: "Detalhamento móvel profissional na sua porta",
    service_boat: "Detalhamento de Barcos",
    service_boat_desc: "Limpeza de grau marinho para todas as embarcações",
    service_rv: "Lavagem de RV",
    service_rv_desc: "Serviço completo de limpeza de RV e motorhomes",
    service_fleet: "Gestão de Frotas",
    service_fleet_desc: "Soluções de lavagem para frotas comerciais",
    
    // How It Works
    how_it_works_title: "Como Funciona",
    how_it_works_subtitle: "Obtenha seu veículo brilhando em 3 passos simples",
    step_1_title: "Reserve Online",
    step_1_desc: "Escolha seu serviço, veículo e horário preferido",
    step_2_title: "Vamos Até Você",
    step_2_desc: "Nosso vendedor verificado chega à sua localização",
    step_3_title: "Aproveite o Brilho",
    step_3_desc: "Relaxe enquanto transformamos seu veículo",
    
    // For Vendors
    vendors_title: "Para Vendedores",
    vendors_subtitle: "Junte-se à nossa rede de detalhadores profissionais",
    vendors_benefit_1: "Defina Seu Próprio Horário",
    vendors_benefit_1_desc: "Trabalhe quando quiser, onde quiser",
    vendors_benefit_2: "Expanda Seu Negócio",
    vendors_benefit_2_desc: "Acesse milhares de clientes na sua área",
    vendors_benefit_3: "Receba Rápido",
    vendors_benefit_3_desc: "Pagamentos semanais diretamente na sua conta",
    vendors_cta: "Torne-se um Vendedor",
    vendors_pricing: "Ver Preços dos Serviços",
    vendors_watch_tutorial: "Ver Vídeo Tutorial",
    
    // Pricing Section
    pricing_title: "Guia de Preços dos Serviços",
    pricing_subtitle: "Preços iniciais para todos os tipos de veículos. Os vendedores definem suas próprias tarifas.",
    pricing_car: "Carro / Sedã",
    pricing_car_types: "Cupês, Sedãs, Hatchbacks",
    pricing_suv: "SUV / Caminhonete",
    pricing_suv_types: "SUVs, Caminhonetes, Vans",
    pricing_boat: "Barcos",
    pricing_boat_types: "Todos os tipos de embarcações",
    pricing_rv: "RVs",
    pricing_rv_types: "Motorhomes e Campers",
    pricing_starting: "A partir de",
    pricing_per_ft: "/pé acima de",
    
    // Add-ons
    addons_title: "Serviços Adicionais Populares",
    addon_vacuum: "Aspiração Interior",
    addon_leather: "Cuidado com Couro",
    addon_wax: "Selante de Cera",
    addon_pet_hair: "Remoção de Pelos de Animais",
    pricing_disclaimer: "*Preços iniciais mostrados. Os vendedores definem suas próprias tarifas. Uma taxa de serviço de rede de 20% se aplica.",
    
    // Vendor Pricing Note
    vendor_pricing_title: "Os Vendedores Definem Seus Próprios Preços",
    vendor_pricing_desc: "Cada vendedor pode personalizar seus preços. Compare vendedores para encontrar as melhores tarifas na sua área.",
    vendor_pricing_tip: "Dica: Preços competitivos atraem mais clientes - se os preços forem muito altos, os clientes podem escolher uma opção mais barata.",
    vendor_pricing_cta: "Painel de Preços do Vendedor",
    
    // Booking Policy
    policy_title: "Política de Reservas e Cancelamentos",
    policy_arrival_title: "Expectativas de Chegada do Vendedor",
    policy_arrival_1: "Os vendedores devem chegar dentro de 30 minutos do horário agendado",
    policy_arrival_2: "Se atrasados, os vendedores devem fornecer atualizações constantes ao cliente",
    policy_arrival_3: "Rastreamento em tempo real disponível quando o vendedor está a caminho",
    policy_cancel_title: "Política de Cancelamento do Cliente",
    policy_cancel_free: "Cancelamento gratuito antes do vendedor aceitar",
    policy_cancel_fee: "Taxa de cancelamento de 20% se cancelado depois que o vendedor está a caminho ou dentro de 20 minutos",
    policy_cancel_brick: "A mesma política se aplica a compromissos em lojas físicas",
    policy_why: "Por que esta política?",
    policy_why_desc: "Valorizamos o tempo de vendedores e clientes. Esta política protege os vendedores de viagens desperdiçadas.",
    
    // Membership
    membership_title: "Assinatura de Cliente",
    membership_subtitle: "Inscreva-se gratuitamente ou faça upgrade para VIP para benefícios extras",
    membership_free: "Grátis",
    membership_free_desc: "Perfeito para lavagens ocasionais",
    membership_vip: "Membro VIP",
    membership_vip_desc: "Para entusiastas de cuidado automotivo",
    membership_feature_booking: "Reserva online fácil",
    membership_feature_vendors: "Acesso a todos os vendedores",
    membership_feature_tracking: "Rastreamento em tempo real",
    membership_feature_support: "Suporte ao cliente",
    membership_feature_priority: "Reserva prioritária",
    membership_feature_discount: "10% de desconto em todos os serviços",
    membership_feature_rewards: "Recompensas exclusivas",
    membership_feature_deals: "Acesso antecipado a ofertas",
    membership_cta_free: "Comece Grátis",
    membership_cta_vip: "Junte-se ao VIP",
    
    // Footer
    footer_pay_per_wash: "Pague apenas quando lavar",
    footer_secure: "Pagamentos seguros",
    footer_platform_fee: "Taxa da plataforma:",
    footer_vip_video: "Veja os Benefícios VIP em Ação",
    
    // Portal Selection
    portal_title: "ESCOLHA SEU PORTAL",
    portal_customer: "Cliente",
    portal_vendor: "Vendedor",
    portal_admin: "Administrador",
    
    // Common
    learn_more: "Saiba Mais",
    contact_us: "Fale Conosco",
    terms: "Termos de Serviço",
    privacy: "Política de Privacidade",
    per_month: "/mês",
    recommended: "Recomendado",
    
    // Admin Dashboard
    admin_dashboard: "Painel de Administração",
    admin_master_access: "Acesso de Administrador Mestre",
    admin_full_control: "Controle total sobre a plataforma WashPros",
    admin_service_map: "Mapa de Serviços",
    admin_crm: "CRM",
    admin_revenue: "Receita",
    admin_pricing: "Preços",
    admin_surge_config: "Config. de Demanda",
    admin_surge_analytics: "Análise de Demanda",
    admin_leaderboard: "Classificação",
    admin_insurance: "Seguros",
    admin_agreements: "Acordos",
    admin_video_center: "Central de Vídeos",
    admin_vendor_analytics: "Análise de Vendedores",
    admin_vendors: "Vendedores",
    admin_customers: "Clientes",
    admin_bookings: "Reservas",
    admin_blog: "Blog",
    admin_social_media: "Redes Sociais",
    admin_notifications: "Notificações",
    admin_settings: "Configurações",
    
    // Admin Stats
    admin_total_customers: "Total de Clientes",
    admin_total_vendors: "Total de Vendedores",
    admin_pending: "pendente",
    admin_total_bookings: "Total de Reservas",
    admin_monthly_revenue: "Receita Mensal",
    admin_active_bookings: "Reservas Ativas",
    admin_completed_today: "Concluídas Hoje",
    admin_pending_approval: "Aguardando Aprovação",
    admin_cancelled: "Canceladas",
    
    // Admin Actions
    admin_view_all: "Ver Tudo",
    admin_approve: "Aprovar",
    admin_reject: "Rejeitar",
    admin_details: "Detalhes",
    admin_edit: "Editar",
    admin_delete: "Excluir",
    admin_save: "Salvar",
    admin_cancel: "Cancelar",
    admin_search: "Pesquisar",
    admin_filter: "Filtrar",
    admin_export: "Exportar",
    admin_refresh: "Atualizar",
    
    // Admin Sections
    admin_quick_actions: "Ações Rápidas",
    admin_recent_activity: "Atividade Recente",
    admin_pending_vendors: "Vendedores Pendentes",
    admin_recent_bookings: "Reservas Recentes",
    admin_platform_settings: "Configurações da Plataforma",
    admin_platform_name: "Nome da Plataforma",
    admin_support_email: "Email de Suporte",
    admin_platform_fee: "Taxa da Plataforma",
    admin_vip_membership: "Preço da Assinatura VIP",
    admin_changes_saved: "Alterações salvas com sucesso",
    admin_save_changes: "Salvar Alterações",
    admin_cancel_changes: "Cancelar Alterações",
    admin_unsaved_changes: "Você tem alterações não salvas",
    
    // Video Center
    admin_video_gallery: "Galeria de Vídeos",
    admin_video_downloads: "Downloads de Vídeos",
    admin_generate_video: "Gerar Vídeo",
    admin_video_status: "Status do Vídeo",
    admin_generating: "Gerando...",
    admin_completed: "Concluído",
    admin_failed: "Falhou",
    
    // CRM
    admin_customer_management: "Gestão de Clientes",
    admin_vendor_management: "Gestão de Vendedores",
    admin_total_revenue: "Receita Total",
    admin_average_order: "Valor Médio do Pedido",
    admin_active_users: "Usuários Ativos",
    admin_vip_members: "Membros VIP",
    admin_basic_tier: "Nível Básico",
    admin_pro_tier: "Nível Pro",
    admin_elite_tier: "Nível Elite",
    
    // Map
    admin_all_locations: "Todas as Localizações",
    admin_customers_only: "Apenas Clientes",
    admin_vendors_only: "Apenas Vendedores",
    admin_map_legend: "Legenda do Mapa",
    admin_basic_customer: "Cliente Básico",
    admin_vip_customer: "Cliente VIP",
    admin_basic_vendor: "Vendedor Básico",
    admin_pro_vendor: "Vendedor Pro",
    admin_elite_vendor: "Vendedor Elite",
    admin_mobile_vendor: "Vendedor Móvel",
    admin_shop_vendor: "Loja/Estabelecimento",
    
    // Insurance
    admin_insurance_verification: "Verificação de Seguros",
    admin_verify_documents: "Verificar Documentos",
    admin_document_status: "Status do Documento",
    admin_verified: "Verificado",
    admin_pending_review: "Em Revisão",
    admin_expired: "Vencido",
    
    // Notifications
    admin_notification_analytics: "Análise de Notificações",
    admin_sent_notifications: "Notificações Enviadas",
    admin_delivery_rate: "Taxa de Entrega",
    admin_open_rate: "Taxa de Abertura",
    
    // Customer Dashboard
    dashboard_welcome: "Bem-vindo de volta,",
    dashboard_home: "Início",
    dashboard_discover: "Descobrir",
    dashboard_rewards: "Recompensas",
    dashboard_history: "Histórico",
    dashboard_inbox: "Caixa",
    dashboard_profile: "Perfil",
    dashboard_current_location: "Localização Atual",
    dashboard_what_needs_washing: "O que precisa de lavagem?",
    dashboard_quick_book: "Reserva Rápida",
    dashboard_book_now: "Reservar Agora",
    dashboard_popular_services: "Serviços Populares",
    dashboard_featured_vendors: "Vendedores em Destaque",
    dashboard_my_vehicles: "Meus Veículos",
    dashboard_add_vehicle: "Adicionar",
    dashboard_no_vehicles: "Nenhum veículo adicionado",
    dashboard_add_first_vehicle: "Adicione Seu Primeiro Veículo",
    dashboard_find_vendors: "Encontrar Vendedores",
    dashboard_see_all: "Ver tudo",
    dashboard_see_how_works: "Veja Como Funciona",
    dashboard_earn_referral: "Ganhe R$50 Por Indicação",
    dashboard_invite_friends: "Convide Amigos e Ganhe",
    dashboard_messages: "Mensagens",
    dashboard_logout: "Sair",
    dashboard_profile_settings: "Configurações do Perfil",
    dashboard_favorites: "Favoritos",
    
    // Vehicle Types
    vehicle_car: "Carro",
    vehicle_boat: "Barco",
    vehicle_rv: "Motorhome",
    vehicle_fleet: "Frota",
    
    // Booking Flow
    booking_select_date: "Selecionar Data",
    booking_select_time: "Selecionar Hora",
    booking_location: "Local do Serviço",
    booking_use_my_location: "Usar Minha Localização Atual",
    booking_search_address: "Buscar Endereço",
    booking_special_instructions: "Instruções Especiais",
    booking_select_vehicle: "Selecionar Veículo",
    booking_choose_package: "Escolha Seu Pacote",
    booking_add_extras: "Adicionar Serviços Extras",
    booking_review_order: "Revisar e Pagar",
    booking_payment: "Pagamento",
    booking_finding_vendor: "Encontrando seu WashPro...",
    booking_vendor_assigned: "Vendedor Designado",
    booking_continue: "Continuar",
    booking_back: "Voltar",
    booking_confirm_booking: "Confirmar Reserva",
    booking_pay_now: "Pagar Agora",
    booking_subtotal: "Subtotal",
    booking_service_fee: "Taxa de Serviço",
    booking_tip: "Gorjeta",
    booking_total: "Total",
    booking_add_tip: "Adicionar Gorjeta",
    booking_no_tip: "Sem Gorjeta",
    booking_custom: "Personalizado",
    
    // Service Packages
    package_basic: "Lavagem Básica",
    package_deluxe: "Lavagem Deluxe",
    package_premium: "Detalhamento Premium",
    package_duration: "min",
    
    // Vendor Portal
    vendor_dashboard: "Painel",
    vendor_jobs: "Trabalhos",
    vendor_pricing: "Preços",
    vendor_rankings: "Classificação",
    vendor_insurance: "Seguro",
    vendor_earnings: "Ganhos",
    vendor_settings: "Configurações",
    vendor_available: "Disponível para Trabalhos",
    vendor_unavailable: "Indisponível",
    vendor_new_job: "Nova Solicitação de Trabalho",
    vendor_accept: "Aceitar",
    vendor_decline: "Recusar",
    vendor_in_progress: "Em Andamento",
    vendor_completed: "Concluído",
    vendor_total_earnings: "Ganhos Totais",
    vendor_this_week: "Esta Semana",
    vendor_this_month: "Este Mês",
    
    // Common Actions
    action_save: "Salvar",
    action_cancel: "Cancelar",
    action_edit: "Editar",
    action_delete: "Excluir",
    action_confirm: "Confirmar",
    action_close: "Fechar",
    action_loading: "Carregando...",
    action_submitting: "Enviando...",
    action_success: "Sucesso!",
    action_error: "Erro",
    action_retry: "Tentar Novamente",
  },
  
  de: {
    // Header
    nav_services: "Dienstleistungen",
    nav_how_it_works: "So Funktioniert's",
    nav_vendors: "Für Anbieter",
    nav_pricing: "Preise",
    nav_login: "Anmelden",
    nav_get_started: "Loslegen",
    
    // Hero Section
    hero_tagline: "WAX AND SHINE ZENTRALE",
    hero_subtitle: "DAS AUTOWÄSCHE-CONCIERGE-UNTERNEHMEN",
    hero_title_1: "Premium Mobile",
    hero_title_2: "Autowäsche",
    hero_title_3: "Auf Abruf",
    hero_description: "Erleben Sie die Zukunft der Autopflege. Professionelle Detailing-Services direkt vor Ihrer Haustür von verifizierten, versicherten Anbietern.",
    hero_cta: "Wäsche Buchen",
    hero_watch_demo: "Demo Ansehen",
    hero_feature_1: "5-Sterne Anbieter",
    hero_feature_2: "Versichert & Verifiziert",
    hero_feature_3: "Auf-Abruf Service",
    
    // Services Section
    services_title: "Unsere Dienstleistungen",
    services_subtitle: "Premium Autopflege-Lösungen für jeden Fahrzeugtyp",
    service_car_wash: "Premium Autowäsche",
    service_car_wash_desc: "Professionelles mobiles Detailing vor Ihrer Haustür",
    service_boat: "Boot Detailing",
    service_boat_desc: "Marine-Qualitätsreinigung für alle Wasserfahrzeuge",
    service_rv: "Wohnmobil Wäsche",
    service_rv_desc: "Vollservice-Reinigung für Wohnmobile",
    service_fleet: "Flottenmanagement",
    service_fleet_desc: "Kommerzielle Flottenwasch-Lösungen",
    
    // How It Works
    how_it_works_title: "So Funktioniert's",
    how_it_works_subtitle: "Ihr Fahrzeug strahlend sauber in 3 einfachen Schritten",
    step_1_title: "Online Buchen",
    step_1_desc: "Wählen Sie Service, Fahrzeug und bevorzugte Zeit",
    step_2_title: "Wir Kommen zu Ihnen",
    step_2_desc: "Unser verifizierter Anbieter kommt zu Ihrem Standort",
    step_3_title: "Genießen Sie den Glanz",
    step_3_desc: "Entspannen Sie sich während wir Ihr Fahrzeug verwandeln",
    
    // Common
    learn_more: "Mehr Erfahren",
    contact_us: "Kontakt",
    terms: "Nutzungsbedingungen",
    privacy: "Datenschutz",
    per_month: "/Monat",
    recommended: "Empfohlen",
    
    // Portal Selection
    portal_title: "PORTAL AUSWÄHLEN",
    portal_customer: "Kunde",
    portal_vendor: "Anbieter",
    portal_admin: "Administrator",
    
    // Customer Dashboard
    dashboard_welcome: "Willkommen zurück,",
    dashboard_home: "Startseite",
    dashboard_discover: "Entdecken",
    dashboard_rewards: "Belohnungen",
    dashboard_history: "Verlauf",
    dashboard_inbox: "Posteingang",
    dashboard_profile: "Profil",
    dashboard_current_location: "Aktueller Standort",
    dashboard_what_needs_washing: "Was muss gewaschen werden?",
    dashboard_quick_book: "Schnellbuchung",
    dashboard_book_now: "Jetzt Buchen",
    dashboard_popular_services: "Beliebte Dienste",
    dashboard_featured_vendors: "Empfohlene Anbieter",
    dashboard_my_vehicles: "Meine Fahrzeuge",
    dashboard_add_vehicle: "Hinzufügen",
    dashboard_no_vehicles: "Keine Fahrzeuge hinzugefügt",
    dashboard_add_first_vehicle: "Erstes Fahrzeug Hinzufügen",
    dashboard_find_vendors: "Anbieter Finden",
    dashboard_see_all: "Alle anzeigen",
    dashboard_see_how_works: "So Funktioniert Es",
    dashboard_earn_referral: "10€ Pro Empfehlung Verdienen",
    dashboard_invite_friends: "Freunde Einladen & Verdienen",
    dashboard_messages: "Nachrichten",
    dashboard_logout: "Abmelden",
    dashboard_profile_settings: "Profileinstellungen",
    dashboard_favorites: "Favoriten",
    
    // Vehicle Types
    vehicle_car: "Auto",
    vehicle_boat: "Boot",
    vehicle_rv: "Wohnmobil",
    vehicle_fleet: "Flotte",
    
    // Booking Flow
    booking_select_date: "Datum Wählen",
    booking_select_time: "Zeit Wählen",
    booking_location: "Servicestandort",
    booking_use_my_location: "Meinen Standort Verwenden",
    booking_search_address: "Adresse Suchen",
    booking_special_instructions: "Besondere Anweisungen",
    booking_select_vehicle: "Fahrzeug Auswählen",
    booking_choose_package: "Paket Wählen",
    booking_add_extras: "Extras Hinzufügen",
    booking_review_order: "Überprüfen & Bezahlen",
    booking_payment: "Zahlung",
    booking_finding_vendor: "WashPro wird gesucht...",
    booking_vendor_assigned: "Anbieter Zugewiesen",
    booking_continue: "Weiter",
    booking_back: "Zurück",
    booking_confirm_booking: "Buchung Bestätigen",
    booking_pay_now: "Jetzt Bezahlen",
    booking_subtotal: "Zwischensumme",
    booking_service_fee: "Servicegebühr",
    booking_tip: "Trinkgeld",
    booking_total: "Gesamt",
    booking_add_tip: "Trinkgeld Hinzufügen",
    booking_no_tip: "Kein Trinkgeld",
    booking_custom: "Benutzerdefiniert",
    
    // Service Packages
    package_basic: "Basis Wäsche",
    package_deluxe: "Deluxe Wäsche",
    package_premium: "Premium Detail",
    package_duration: "Min",
    
    // Vendor Portal
    vendor_dashboard: "Dashboard",
    vendor_jobs: "Aufträge",
    vendor_pricing: "Preise",
    vendor_rankings: "Rangliste",
    vendor_insurance: "Versicherung",
    vendor_earnings: "Einnahmen",
    vendor_settings: "Einstellungen",
    vendor_available: "Verfügbar für Aufträge",
    vendor_unavailable: "Nicht Verfügbar",
    vendor_new_job: "Neue Auftragsanfrage",
    vendor_accept: "Annehmen",
    vendor_decline: "Ablehnen",
    vendor_in_progress: "In Bearbeitung",
    vendor_completed: "Abgeschlossen",
    vendor_total_earnings: "Gesamteinnahmen",
    vendor_this_week: "Diese Woche",
    vendor_this_month: "Diesen Monat",
    
    // Common Actions
    action_save: "Speichern",
    action_cancel: "Abbrechen",
    action_edit: "Bearbeiten",
    action_delete: "Löschen",
    action_confirm: "Bestätigen",
    action_close: "Schließen",
    action_loading: "Wird geladen...",
    action_submitting: "Wird gesendet...",
    action_success: "Erfolg!",
    action_error: "Fehler",
    action_retry: "Erneut versuchen",
  },
  
  zh: {
    // Header
    nav_services: "服务",
    nav_how_it_works: "如何运作",
    nav_vendors: "供应商",
    nav_pricing: "价格",
    nav_login: "登录",
    nav_get_started: "开始",
    
    // Hero Section
    hero_tagline: "打蜡和光泽总部",
    hero_subtitle: "汽车清洗礼宾公司",
    hero_title_1: "高级移动",
    hero_title_2: "汽车清洗",
    hero_title_3: "按需服务",
    hero_description: "体验汽车护理的未来。由经过验证的、有保险的供应商提供专业的美容服务，直接送到您家门口。",
    hero_cta: "预约清洗",
    hero_watch_demo: "观看演示",
    hero_feature_1: "五星级供应商",
    hero_feature_2: "保险及验证",
    hero_feature_3: "按需服务",
    
    // Services Section
    services_title: "我们的服务",
    services_subtitle: "为每种车型提供高级汽车护理解决方案",
    service_car_wash: "高级汽车清洗",
    service_car_wash_desc: "专业移动美容服务送到您家门口",
    service_boat: "船只美容",
    service_boat_desc: "所有水上设备的船舶级清洁",
    service_rv: "房车清洗",
    service_rv_desc: "全方位房车和露营车清洁服务",
    service_fleet: "车队管理",
    service_fleet_desc: "商业车队清洗解决方案",
    
    // How It Works
    how_it_works_title: "如何运作",
    how_it_works_subtitle: "3个简单步骤让您的车辆焕然一新",
    step_1_title: "在线预约",
    step_1_desc: "选择您的服务、车辆和首选时间",
    step_2_title: "我们上门服务",
    step_2_desc: "我们经过验证的供应商到达您的位置",
    step_3_title: "享受光泽",
    step_3_desc: "在我们改造您的车辆时放松",
    
    // Common
    learn_more: "了解更多",
    contact_us: "联系我们",
    terms: "服务条款",
    privacy: "隐私政策",
    per_month: "/月",
    recommended: "推荐",
    
    // Portal Selection
    portal_title: "选择您的门户",
    portal_customer: "客户",
    portal_vendor: "供应商",
    portal_admin: "管理员",
    
    // Customer Dashboard
    dashboard_welcome: "欢迎回来，",
    dashboard_home: "首页",
    dashboard_discover: "发现",
    dashboard_rewards: "奖励",
    dashboard_history: "历史",
    dashboard_inbox: "收件箱",
    dashboard_profile: "个人资料",
    dashboard_current_location: "当前位置",
    dashboard_what_needs_washing: "需要清洗什么？",
    dashboard_quick_book: "快速预订",
    dashboard_book_now: "立即预订",
    dashboard_popular_services: "热门服务",
    dashboard_featured_vendors: "推荐供应商",
    dashboard_my_vehicles: "我的车辆",
    dashboard_add_vehicle: "添加",
    dashboard_no_vehicles: "尚未添加车辆",
    dashboard_add_first_vehicle: "添加您的第一辆车",
    dashboard_find_vendors: "查找供应商",
    dashboard_see_all: "查看全部",
    dashboard_see_how_works: "了解如何运作",
    dashboard_earn_referral: "推荐赚取¥50",
    dashboard_invite_friends: "邀请朋友赚钱",
    dashboard_messages: "消息",
    dashboard_logout: "登出",
    dashboard_profile_settings: "个人资料设置",
    dashboard_favorites: "收藏夹",
    
    // Vehicle Types
    vehicle_car: "汽车",
    vehicle_boat: "船",
    vehicle_rv: "房车",
    vehicle_fleet: "车队",
    
    // Booking Flow
    booking_select_date: "选择日期",
    booking_select_time: "选择时间",
    booking_location: "服务地点",
    booking_use_my_location: "使用当前位置",
    booking_search_address: "搜索地址",
    booking_special_instructions: "特殊说明",
    booking_select_vehicle: "选择车辆",
    booking_choose_package: "选择套餐",
    booking_add_extras: "添加额外服务",
    booking_review_order: "审核并付款",
    booking_payment: "付款",
    booking_finding_vendor: "正在寻找您的WashPro...",
    booking_vendor_assigned: "供应商已分配",
    booking_continue: "继续",
    booking_back: "返回",
    booking_confirm_booking: "确认预订",
    booking_pay_now: "立即支付",
    booking_subtotal: "小计",
    booking_service_fee: "服务费",
    booking_tip: "小费",
    booking_total: "总计",
    booking_add_tip: "添加小费",
    booking_no_tip: "无小费",
    booking_custom: "自定义",
    
    // Service Packages
    package_basic: "基础清洗",
    package_deluxe: "豪华清洗",
    package_premium: "高级美容",
    package_duration: "分钟",
    
    // Vendor Portal
    vendor_dashboard: "仪表板",
    vendor_jobs: "工作",
    vendor_pricing: "定价",
    vendor_rankings: "排名",
    vendor_insurance: "保险",
    vendor_earnings: "收入",
    vendor_settings: "设置",
    vendor_available: "可接单",
    vendor_unavailable: "不可用",
    vendor_new_job: "新工作请求",
    vendor_accept: "接受",
    vendor_decline: "拒绝",
    vendor_in_progress: "进行中",
    vendor_completed: "已完成",
    vendor_total_earnings: "总收入",
    vendor_this_week: "本周",
    vendor_this_month: "本月",
    
    // Common Actions
    action_save: "保存",
    action_cancel: "取消",
    action_edit: "编辑",
    action_delete: "删除",
    action_confirm: "确认",
    action_close: "关闭",
    action_loading: "加载中...",
    action_submitting: "提交中...",
    action_success: "成功！",
    action_error: "错误",
    action_retry: "重试",
  },
  
  ja: {
    // Header
    nav_services: "サービス",
    nav_how_it_works: "ご利用方法",
    nav_vendors: "ベンダー向け",
    nav_pricing: "料金",
    nav_login: "ログイン",
    nav_get_started: "始める",
    
    // Hero Section
    hero_tagline: "ワックス＆シャイン本部",
    hero_subtitle: "カーウォッシュコンシェルジュ会社",
    hero_title_1: "プレミアムモバイル",
    hero_title_2: "カーウォッシュ",
    hero_title_3: "オンデマンド",
    hero_description: "カーケアの未来を体験してください。認証済みの保険付きベンダーによるプロのディテーリングサービスがあなたの玄関先に届きます。",
    hero_cta: "洗車を予約",
    hero_watch_demo: "デモを見る",
    hero_feature_1: "5つ星ベンダー",
    hero_feature_2: "保険完備＆認証済み",
    hero_feature_3: "オンデマンドサービス",
    
    // Services Section
    services_title: "サービス",
    services_subtitle: "あらゆる車種に対応したプレミアムカーケアソリューション",
    service_car_wash: "プレミアムカーウォッシュ",
    service_car_wash_desc: "ご自宅でプロのモバイルディテーリング",
    service_boat: "ボートディテーリング",
    service_boat_desc: "すべての船舶用マリングレードクリーニング",
    service_rv: "RV洗車",
    service_rv_desc: "RVとキャンピングカーのフルサービスクリーニング",
    service_fleet: "フリート管理",
    service_fleet_desc: "商用フリート洗車ソリューション",
    
    // How It Works
    how_it_works_title: "ご利用方法",
    how_it_works_subtitle: "3つの簡単なステップで車をピカピカに",
    step_1_title: "オンライン予約",
    step_1_desc: "サービス、車両、ご希望の時間を選択",
    step_2_title: "お客様のもとへ",
    step_2_desc: "認証済みベンダーがお客様の場所に到着",
    step_3_title: "輝きをお楽しみください",
    step_3_desc: "私たちがあなたの車を変身させる間、リラックスしてください",
    
    // Common
    learn_more: "詳細",
    contact_us: "お問い合わせ",
    terms: "利用規約",
    privacy: "プライバシーポリシー",
    per_month: "/月",
    recommended: "おすすめ",
    
    // Portal Selection
    portal_title: "ポータルを選択",
    portal_customer: "お客様",
    portal_vendor: "ベンダー",
    portal_admin: "管理者",
    
    // Customer Dashboard
    dashboard_welcome: "おかえりなさい、",
    dashboard_home: "ホーム",
    dashboard_discover: "発見",
    dashboard_rewards: "リワード",
    dashboard_history: "履歴",
    dashboard_inbox: "受信箱",
    dashboard_profile: "プロフィール",
    dashboard_current_location: "現在地",
    dashboard_what_needs_washing: "何を洗いますか？",
    dashboard_quick_book: "クイック予約",
    dashboard_book_now: "今すぐ予約",
    dashboard_popular_services: "人気のサービス",
    dashboard_featured_vendors: "注目のベンダー",
    dashboard_my_vehicles: "マイ車両",
    dashboard_add_vehicle: "追加",
    dashboard_no_vehicles: "車両が追加されていません",
    dashboard_add_first_vehicle: "最初の車両を追加",
    dashboard_find_vendors: "ベンダーを探す",
    dashboard_see_all: "すべて見る",
    dashboard_see_how_works: "使い方を見る",
    dashboard_earn_referral: "紹介で¥1000獲得",
    dashboard_invite_friends: "友達を招待して稼ぐ",
    dashboard_messages: "メッセージ",
    dashboard_logout: "ログアウト",
    dashboard_profile_settings: "プロフィール設定",
    dashboard_favorites: "お気に入り",
    
    // Vehicle Types
    vehicle_car: "車",
    vehicle_boat: "ボート",
    vehicle_rv: "キャンピングカー",
    vehicle_fleet: "フリート",
    
    // Booking Flow
    booking_select_date: "日付を選択",
    booking_select_time: "時間を選択",
    booking_location: "サービス場所",
    booking_use_my_location: "現在地を使用",
    booking_search_address: "住所を検索",
    booking_special_instructions: "特別な指示",
    booking_select_vehicle: "車両を選択",
    booking_choose_package: "パッケージを選択",
    booking_add_extras: "追加サービス",
    booking_review_order: "確認＆支払い",
    booking_payment: "支払い",
    booking_finding_vendor: "WashProを探しています...",
    booking_vendor_assigned: "ベンダー割り当て済み",
    booking_continue: "続ける",
    booking_back: "戻る",
    booking_confirm_booking: "予約を確認",
    booking_pay_now: "今すぐ支払う",
    booking_subtotal: "小計",
    booking_service_fee: "サービス料",
    booking_tip: "チップ",
    booking_total: "合計",
    booking_add_tip: "チップを追加",
    booking_no_tip: "チップなし",
    booking_custom: "カスタム",
    
    // Service Packages
    package_basic: "ベーシック洗車",
    package_deluxe: "デラックス洗車",
    package_premium: "プレミアムディテール",
    package_duration: "分",
    
    // Vendor Portal
    vendor_dashboard: "ダッシュボード",
    vendor_jobs: "ジョブ",
    vendor_pricing: "価格設定",
    vendor_rankings: "ランキング",
    vendor_insurance: "保険",
    vendor_earnings: "収益",
    vendor_settings: "設定",
    vendor_available: "受付可能",
    vendor_unavailable: "受付不可",
    vendor_new_job: "新規ジョブリクエスト",
    vendor_accept: "承諾",
    vendor_decline: "辞退",
    vendor_in_progress: "進行中",
    vendor_completed: "完了",
    vendor_total_earnings: "総収益",
    vendor_this_week: "今週",
    vendor_this_month: "今月",
    
    // Common Actions
    action_save: "保存",
    action_cancel: "キャンセル",
    action_edit: "編集",
    action_delete: "削除",
    action_confirm: "確認",
    action_close: "閉じる",
    action_loading: "読み込み中...",
    action_submitting: "送信中...",
    action_success: "成功！",
    action_error: "エラー",
    action_retry: "再試行",
  },
  
  ko: {
    // Header
    nav_services: "서비스",
    nav_how_it_works: "이용 방법",
    nav_vendors: "벤더용",
    nav_pricing: "가격",
    nav_login: "로그인",
    nav_get_started: "시작하기",
    
    // Hero Section
    hero_tagline: "왁스 앤 샤인 본부",
    hero_subtitle: "세차 컨시어지 회사",
    hero_title_1: "프리미엄 모바일",
    hero_title_2: "세차",
    hero_title_3: "온디맨드",
    hero_description: "자동차 관리의 미래를 경험하세요. 인증되고 보험에 가입된 공급업체가 전문 디테일링 서비스를 문앞까지 제공합니다.",
    hero_cta: "세차 예약",
    hero_watch_demo: "데모 보기",
    hero_feature_1: "5성 공급업체",
    hero_feature_2: "보험 및 인증",
    hero_feature_3: "온디맨드 서비스",
    
    // Services Section
    services_title: "서비스",
    services_subtitle: "모든 차량 유형을 위한 프리미엄 자동차 관리 솔루션",
    service_car_wash: "프리미엄 세차",
    service_car_wash_desc: "문앞까지 전문 모바일 디테일링",
    service_boat: "보트 디테일링",
    service_boat_desc: "모든 선박용 해양급 청소",
    service_rv: "RV 세차",
    service_rv_desc: "RV 및 캠핑카 풀 서비스 청소",
    service_fleet: "차량 관리",
    service_fleet_desc: "상업용 차량 세차 솔루션",
    
    // How It Works
    how_it_works_title: "이용 방법",
    how_it_works_subtitle: "3단계로 차량을 반짝이게",
    step_1_title: "온라인 예약",
    step_1_desc: "서비스, 차량 및 원하는 시간 선택",
    step_2_title: "방문 서비스",
    step_2_desc: "인증된 공급업체가 귀하의 위치에 도착",
    step_3_title: "광택을 즐기세요",
    step_3_desc: "차량을 변신시키는 동안 편히 쉬세요",
    
    // Common
    learn_more: "더 알아보기",
    contact_us: "문의하기",
    terms: "이용약관",
    privacy: "개인정보 처리방침",
    per_month: "/월",
    recommended: "추천",
    
    // Portal Selection
    portal_title: "포털 선택",
    portal_customer: "고객",
    portal_vendor: "공급업체",
    portal_admin: "관리자",
    
    // Customer Dashboard
    dashboard_welcome: "다시 오셨군요,",
    dashboard_home: "홈",
    dashboard_discover: "발견",
    dashboard_rewards: "리워드",
    dashboard_history: "내역",
    dashboard_inbox: "받은편지함",
    dashboard_profile: "프로필",
    dashboard_current_location: "현재 위치",
    dashboard_what_needs_washing: "무엇을 세차할까요?",
    dashboard_quick_book: "빠른 예약",
    dashboard_book_now: "지금 예약",
    dashboard_popular_services: "인기 서비스",
    dashboard_featured_vendors: "추천 공급업체",
    dashboard_my_vehicles: "내 차량",
    dashboard_add_vehicle: "추가",
    dashboard_no_vehicles: "추가된 차량이 없습니다",
    dashboard_add_first_vehicle: "첫 번째 차량 추가",
    dashboard_find_vendors: "공급업체 찾기",
    dashboard_see_all: "모두 보기",
    dashboard_see_how_works: "이용 방법 보기",
    dashboard_earn_referral: "추천으로 ₩10,000 벌기",
    dashboard_invite_friends: "친구 초대하고 수익 얻기",
    dashboard_messages: "메시지",
    dashboard_logout: "로그아웃",
    dashboard_profile_settings: "프로필 설정",
    dashboard_favorites: "즐겨찾기",
    
    // Vehicle Types
    vehicle_car: "자동차",
    vehicle_boat: "보트",
    vehicle_rv: "캠핑카",
    vehicle_fleet: "차량단",
    
    // Booking Flow
    booking_select_date: "날짜 선택",
    booking_select_time: "시간 선택",
    booking_location: "서비스 위치",
    booking_use_my_location: "현재 위치 사용",
    booking_search_address: "주소 검색",
    booking_special_instructions: "특별 지시사항",
    booking_select_vehicle: "차량 선택",
    booking_choose_package: "패키지 선택",
    booking_add_extras: "추가 서비스",
    booking_review_order: "확인 및 결제",
    booking_payment: "결제",
    booking_finding_vendor: "WashPro를 찾는 중...",
    booking_vendor_assigned: "공급업체 배정됨",
    booking_continue: "계속",
    booking_back: "뒤로",
    booking_confirm_booking: "예약 확인",
    booking_pay_now: "지금 결제",
    booking_subtotal: "소계",
    booking_service_fee: "서비스 수수료",
    booking_tip: "팁",
    booking_total: "합계",
    booking_add_tip: "팁 추가",
    booking_no_tip: "팁 없음",
    booking_custom: "사용자 지정",
    
    // Service Packages
    package_basic: "기본 세차",
    package_deluxe: "디럭스 세차",
    package_premium: "프리미엄 디테일링",
    package_duration: "분",
    
    // Vendor Portal
    vendor_dashboard: "대시보드",
    vendor_jobs: "작업",
    vendor_pricing: "가격",
    vendor_rankings: "순위",
    vendor_insurance: "보험",
    vendor_earnings: "수익",
    vendor_settings: "설정",
    vendor_available: "작업 가능",
    vendor_unavailable: "작업 불가",
    vendor_new_job: "새 작업 요청",
    vendor_accept: "수락",
    vendor_decline: "거절",
    vendor_in_progress: "진행 중",
    vendor_completed: "완료됨",
    vendor_total_earnings: "총 수익",
    vendor_this_week: "이번 주",
    vendor_this_month: "이번 달",
    
    // Common Actions
    action_save: "저장",
    action_cancel: "취소",
    action_edit: "편집",
    action_delete: "삭제",
    action_confirm: "확인",
    action_close: "닫기",
    action_loading: "로딩 중...",
    action_submitting: "제출 중...",
    action_success: "성공!",
    action_error: "오류",
    action_retry: "다시 시도",
  },
  it: {
    // Header
    nav_services: "Servizi",
    nav_how_it_works: "Come Funziona",
    nav_vendors: "Per i Fornitori",
    nav_pricing: "Prezzi",
    nav_login: "Accedi",
    nav_get_started: "Inizia Ora",
    
    // Hero Section
    hero_tagline: "WAX AND SHINE HEADQUARTERS™",
    hero_subtitle: "THE CARWASH CONCIERGE COMPANY™",
    hero_title_1: "Lavaggio Auto",
    hero_title_2: "Mobile Premium",
    hero_title_3: "Su Richiesta",
    hero_description: "Scopra il futuro della cura dell'auto. Servizi di detailing professionale consegnati a domicilio da fornitori verificati e assicurati.",
    hero_cta: "Prenoti il Lavaggio",
    hero_watch_demo: "Guarda la Demo",
    hero_feature_1: "Fornitori con 5 Stelle",
    hero_feature_2: "Assicurati e Verificati",
    hero_feature_3: "Servizio On Demand",
    
    // Services Section
    services_title: "I Nostri Servizi",
    services_subtitle: "Soluzioni premium per la cura di ogni tipo di veicolo",
    service_car_wash: "Lavaggio Auto Premium",
    service_car_wash_desc: "Detailing mobile professionale a domicilio",
    service_boat: "Detailing Imbarcazioni",
    service_boat_desc: "Pulizia di qualità marina per ogni tipo di natante",
    service_rv: "Lavaggio Camper",
    service_rv_desc: "Pulizia completa di camper e motorhome",
    service_fleet: "Gestione Flotte",
    service_fleet_desc: "Soluzioni di lavaggio per flotte commerciali",
    
    // How It Works
    how_it_works_title: "Come Funziona",
    how_it_works_subtitle: "Ottenga un veicolo splendente in 3 semplici passi",
    step_1_title: "Prenoti Online",
    step_1_desc: "Scelga il servizio, il veicolo e la fascia oraria preferita",
    step_2_title: "Veniamo da Lei",
    step_2_desc: "Il nostro fornitore verificato raggiunge la Sua posizione",
    step_3_title: "Goda della Lucentezza",
    step_3_desc: "Si rilassi mentre trasformiamo il Suo veicolo",
    
    // For Vendors
    vendors_title: "Per i Fornitori",
    vendors_subtitle: "Si unisca alla nostra rete di detailer professionisti",
    vendors_benefit_1: "Gestisca i Suoi Orari",
    vendors_benefit_1_desc: "Lavori quando e dove vuole",
    vendors_benefit_2: "Faccia Crescere il Suo Business",
    vendors_benefit_2_desc: "Acceda a migliaia di clienti nella Sua area",
    vendors_benefit_3: "Pagamenti Rapidi",
    vendors_benefit_3_desc: "Pagamenti settimanali direttamente sul Suo conto",
    vendors_cta: "Diventa Fornitore",
    vendors_pricing: "Visualizza i Prezzi dei Servizi",
    vendors_watch_tutorial: "Guarda il Video Tutorial",
    
    // Pricing Section
    pricing_title: "Guida ai Prezzi dei Servizi",
    pricing_subtitle: "Prezzi di partenza per tutti i tipi di veicolo. I fornitori stabiliscono le proprie tariffe — trovi il prezzo migliore per le Sue esigenze.",
    pricing_car: "Auto / Berlina",
    pricing_car_types: "Coupé, Berline, Hatchback",
    pricing_suv: "SUV / Pick-up",
    pricing_suv_types: "SUV, Pick-up, Van",
    pricing_boat: "Imbarcazioni",
    pricing_boat_types: "Tutti i tipi di natante",
    pricing_rv: "Camper",
    pricing_rv_types: "Motorhome e Caravan",
    pricing_starting: "A partire da",
    pricing_per_ft: "/ft oltre",
    
    // Add-ons
    addons_title: "Servizi Aggiuntivi Più Richiesti",
    addon_vacuum: "Aspirazione Interni",
    addon_leather: "Trattamento Pelle",
    addon_wax: "Sigillante a Cera",
    addon_pet_hair: "Rimozione Peli di Animali",
    pricing_disclaimer: "*Prezzi di partenza indicativi. I fornitori stabiliscono le proprie tariffe. A tutte le prenotazioni si applica una commissione di servizio del 20%.",
    
    // Vendor Pricing Note
    vendor_pricing_title: "I Fornitori Stabiliscono i Propri Prezzi",
    vendor_pricing_desc: "Ogni fornitore può personalizzare le proprie tariffe. Confronti i fornitori per trovare le migliori tariffe nella Sua area.",
    vendor_pricing_tip: "Consiglio: prezzi competitivi attraggono più clienti — se le tariffe sono troppo elevate, i clienti potrebbero scegliere un'opzione più conveniente.",
    vendor_pricing_cta: "Dashboard Prezzi Fornitori",
    
    // Booking Policy
    policy_title: "Politica di Prenotazione e Cancellazione",
    policy_arrival_title: "Aspettative sull'Arrivo del Fornitore",
    policy_arrival_1: "I fornitori devono arrivare entro 30 minuti dall'orario dell'appuntamento programmato",
    policy_arrival_2: "In caso di ritardo, i fornitori sono tenuti a fornire aggiornamenti costanti sullo stato al cliente",
    policy_arrival_3: "Tracciamento in tempo reale disponibile non appena il fornitore è in viaggio",
    policy_cancel_title: "Politica di Cancellazione per il Cliente",
    policy_cancel_free: "Cancellazione gratuita prima che il fornitore accetti la prenotazione",
    policy_cancel_fee: "Commissione di cancellazione del 20% se annullata dopo che il fornitore è in viaggio o entro 20 minuti dall'accettazione della prenotazione",
    policy_cancel_brick: "La stessa politica si applica agli appuntamenti presso negozi fisici",
    policy_why: "Perché questa politica?",
    policy_why_desc: "Valorizziamo il tempo sia dei nostri fornitori che dei nostri clienti. Questa politica protegge i fornitori da spostamenti e costi di carburante inutili, incoraggiando al contempo i clienti a rispettare gli appuntamenti programmati.",
    
    // Membership
    membership_title: "Abbonamento Cliente",
    membership_subtitle: "Iscriviti gratuitamente o passa al VIP per vantaggi esclusivi",
    membership_free: "Gratuito",
    membership_free_desc: "Perfetto per lavaggi occasionali",
    membership_vip: "Membro VIP",
    membership_vip_desc: "Per gli appassionati della cura dell'auto",
    membership_feature_booking: "Prenotazione online semplice",
    membership_feature_vendors: "Accesso a tutti i fornitori",
    membership_feature_tracking: "Monitoraggio in tempo reale",
    membership_feature_support: "Assistenza clienti",
    membership_feature_priority: "Prenotazione prioritaria",
    membership_feature_discount: "10% di sconto su tutti i servizi",
    membership_feature_rewards: "Premi esclusivi",
    membership_feature_deals: "Accesso anticipato alle offerte",
    membership_cta_free: "Inizia Gratis",
    membership_cta_vip: "Diventa VIP",
    
    // Footer
    footer_pay_per_wash: "Paghi solo quando lavi",
    footer_secure: "Pagamenti sicuri",
    footer_platform_fee: "Commissione di piattaforma:",
    footer_vip_video: "Scopri i Vantaggi VIP in Azione",
    
    // Portal Selection
    portal_title: "SCEGLI IL TUO PORTALE",
    portal_customer: "Cliente",
    portal_vendor: "Fornitore",
    portal_admin: "Amministratore",
    
    // Common
    learn_more: "Scopri di Più",
    contact_us: "Contattaci",
    terms: "Termini di Servizio",
    privacy: "Informativa sulla Privacy",
    per_month: "/mese",
    recommended: "Consigliato",
    
    // Service History & Recurring
    service_history: "Cronologia Servizi",
    past_bookings: "Le Tue Prenotazioni Passate",
    recurring_bookings: "Prenotazioni Ricorrenti",
    no_bookings: "Nessuna prenotazione ancora",
    rebook_service: "Riprenota Questo Servizio",
    make_recurring: "Rendi Ricorrente",
    add_tip: "Aggiungi Mancia",
    tip_added: "Mancia Aggiunta",
    every_x_days: "Ogni {days} giorni",
    pause: "Metti in Pausa",
    resume: "Riprendi",
    cancel_recurring: "Annulla Ricorrente",
    
    // Tip Collection
    add_a_tip: "Aggiungi una Mancia",
    show_appreciation: "Esprimi il Tuo Apprezzamento",
    tip_goes_to_vendor: "Il 100% della tua mancia va direttamente a",
    send_tip: "Invia Mancia",
    skip_for_now: "Salta per ora",
    thank_you: "Grazie!",
    tip_sent: "La tua mancia è stata inviata",
    
    // Fleet Contact
    fleet_management: "Gestione Flotta",
    get_custom_pricing: "Ottieni un preventivo personalizzato per la tua azienda",
    company_info: "Informazioni Aziendali",
    company_name: "Nome Azienda",
    contact_name: "Nome Referente",
    fleet_details: "Dettagli della Flotta",
    vehicle_types: "Tipologie di Veicoli nella Flotta",
    fleet_size: "Dimensione della Flotta",
    service_frequency: "Frequenza di Servizio Desiderata",
    submit_inquiry: "Invia Richiesta Flotta",
    inquiry_submitted: "Richiesta Inviata!",
    
    // Shop Finder
    find_shop: "Trova un Centro",
    nearby_shops: "Autolavaggi nelle Vicinanze",
    book_appointment: "Prenota un Appuntamento",
    select_date: "Seleziona Data",
    select_time: "Seleziona Orario",
    available_slots: "Slot Disponibili",
    confirm_booking: "Conferma Prenotazione",
    
    // Admin Dashboard
    admin_dashboard: "Pannello di Amministrazione",
    admin_master_access: "Accesso Amministratore Principale",
    admin_full_control: "Controllo completo sulla piattaforma WashPros",
    admin_service_map: "Mappa dei Servizi",
    admin_crm: "CRM",
    admin_revenue: "Ricavi",
    admin_pricing: "Tariffe",
    admin_surge_config: "Configurazione Surge",
    admin_surge_analytics: "Analisi Surge",
    admin_leaderboard: "Classifica",
    admin_insurance: "Assicurazione",
    admin_agreements: "Accordi",
    admin_video_center: "Centro Video",
    admin_vendor_analytics: "Analisi Fornitori",
    admin_vendors: "Fornitori",
    admin_customers: "Clienti",
    admin_bookings: "Prenotazioni",
    admin_blog: "Blog",
    admin_social_media: "Social Media",
    admin_notifications: "Notifiche",
    admin_settings: "Impostazioni",
    
    // Admin Stats
    admin_total_customers: "Clienti Totali",
    admin_total_vendors: "Fornitori Totali",
    admin_pending: "in attesa",
    admin_total_bookings: "Prenotazioni Totali",
    admin_monthly_revenue: "Fatturato Mensile",
    admin_active_bookings: "Prenotazioni Attive",
    admin_completed_today: "Completate Oggi",
    admin_pending_approval: "In Attesa di Approvazione",
    admin_cancelled: "Annullate",
    
    // Admin Actions
    admin_view_all: "Visualizza Tutto",
    admin_approve: "Approva",
    admin_reject: "Rifiuta",
    admin_details: "Dettagli",
    admin_edit: "Modifica",
    admin_delete: "Elimina",
    admin_save: "Salva",
    admin_cancel: "Annulla",
    admin_search: "Cerca",
    admin_filter: "Filtra",
    admin_export: "Esporta",
    admin_refresh: "Aggiorna",
    
    // Admin Sections
    admin_quick_actions: "Azioni Rapide",
    admin_recent_activity: "Attività Recente",
    admin_pending_vendors: "Fornitori in Attesa",
    admin_recent_bookings: "Prenotazioni Recenti",
    admin_platform_settings: "Impostazioni Piattaforma",
    admin_platform_name: "Nome Piattaforma",
    admin_support_email: "Email di Supporto",
    admin_platform_fee: "Commissione Piattaforma",
    admin_vip_membership: "Prezzo Abbonamento VIP",
    admin_changes_saved: "Modifiche salvate con successo",
    admin_save_changes: "Salva Modifiche",
    admin_cancel_changes: "Annulla Modifiche",
    admin_unsaved_changes: "Sono presenti modifiche non salvate",
    
    // Video Center
    admin_video_gallery: "Galleria Video",
    admin_video_downloads: "Download Video",
    admin_generate_video: "Genera Video",
    admin_video_status: "Stato Video",
    admin_generating: "Generazione in corso...",
    admin_completed: "Completato",
    admin_failed: "Non riuscito",
    
    // CRM
    admin_customer_management: "Gestione Clienti",
    admin_vendor_management: "Gestione Fornitori",
    admin_total_revenue: "Fatturato Totale",
    admin_average_order: "Valore Medio Ordine",
    admin_active_users: "Utenti Attivi",
    admin_vip_members: "Membri VIP",
    admin_basic_tier: "Livello Base",
    admin_pro_tier: "Livello Pro",
    admin_elite_tier: "Livello Elite",
    
    // Map
    admin_all_locations: "Tutte le Sedi",
    admin_customers_only: "Solo Clienti",
    admin_vendors_only: "Solo Fornitori",
    admin_map_legend: "Legenda Mappa",
    admin_basic_customer: "Cliente Base",
    admin_vip_customer: "Cliente VIP",
    admin_basic_vendor: "Fornitore Base",
    admin_pro_vendor: "Fornitore Pro",
    admin_elite_vendor: "Fornitore Elite",
    admin_mobile_vendor: "Fornitore Mobile",
    admin_shop_vendor: "Negozio/Sede Fisica",
    
    // Insurance
    admin_insurance_verification: "Verifica Assicurazione",
    admin_verify_documents: "Verifica Documenti",
    admin_document_status: "Stato Documento",
    admin_verified: "Verificato",
    admin_pending_review: "In Attesa di Revisione",
    admin_expired: "Scaduto",
    
    // Notifications
    admin_notification_analytics: "Analisi Notifiche",
    admin_sent_notifications: "Notifiche Inviate",
    admin_delivery_rate: "Tasso di Consegna",
    admin_open_rate: "Tasso di Apertura",
    
    // Customer Dashboard
    dashboard_welcome: "Bentornato/a,",
    dashboard_home: "Home",
    dashboard_discover: "Scopri",
    dashboard_rewards: "Premi",
    dashboard_history: "Cronologia",
    dashboard_inbox: "Posta in arrivo",
    dashboard_profile: "Profilo",
    dashboard_current_location: "Posizione attuale",
    dashboard_what_needs_washing: "Cosa ha bisogno di un lavaggio?",
    dashboard_quick_book: "Prenota rapido",
    dashboard_book_now: "Prenota ora",
    dashboard_popular_services: "Servizi popolari",
    dashboard_featured_vendors: "Fornitori in evidenza",
    dashboard_my_vehicles: "I miei veicoli",
    dashboard_add_vehicle: "Aggiungi",
    dashboard_no_vehicles: "Nessun veicolo aggiunto",
    dashboard_add_first_vehicle: "Aggiungi il primo veicolo",
    dashboard_find_vendors: "Trova fornitori",
    dashboard_see_all: "Vedi tutti",
    dashboard_see_how_works: "Scopri come funziona",
    dashboard_earn_referral: "Guadagna $10 per ogni referral",
    dashboard_invite_friends: "Invita amici e guadagna",
    dashboard_messages: "Messaggi",
    dashboard_logout: "Esci",
    dashboard_profile_settings: "Impostazioni profilo",
    dashboard_favorites: "Preferiti",
    
    // Vehicle Types
    vehicle_car: "Auto",
    vehicle_boat: "Barca",
    vehicle_rv: "RV",
    vehicle_fleet: "Flotta",
    vehicle_golf_cart: "Golf cart",
    vehicle_recreational: "Ricreativo",
    vehicle_luxury: "Luxury",
    
    // Booking Flow
    booking_select_date: "Seleziona data",
    booking_select_time: "Seleziona orario",
    booking_location: "Luogo del servizio",
    booking_use_my_location: "Usa la mia posizione attuale",
    booking_search_address: "Cerca indirizzo",
    booking_special_instructions: "Istruzioni speciali",
    booking_select_vehicle: "Seleziona veicolo",
    booking_choose_package: "Scegli il pacchetto",
    booking_add_extras: "Aggiungi servizi extra",
    booking_review_order: "Riepilogo e pagamento",
    booking_payment: "Pagamento",
    booking_finding_vendor: "Ricerca del suo WashPro in corso...",
    booking_vendor_assigned: "Fornitore assegnato",
    booking_continue: "Continua",
    booking_back: "Indietro",
    booking_confirm_booking: "Conferma prenotazione",
    booking_pay_now: "Paga ora",
    booking_subtotal: "Subtotale",
    booking_service_fee: "Commissione di servizio",
    booking_tip: "Mancia",
    booking_total: "Totale",
    booking_add_tip: "Aggiungi una mancia",
    booking_no_tip: "Nessuna mancia",
    booking_custom: "Personalizzato",
    
    // Service Packages
    package_basic: "Lavaggio base",
    package_deluxe: "Lavaggio deluxe",
    package_premium: "Detailing premium",
    package_duration: "min",
    
    // New Booking Flow Translations
    booking_premium_mobile: "Detailing mobile premium",
    booking_on_demand: "Lavaggio su richiesta",
    booking_on_demand_desc: "Lavaggio professionale quando ne ha bisogno",
    booking_we_come: "Veniamo da Lei",
    booking_we_come_desc: "Servizio a casa, in ufficio o ovunque Lei voglia",
    booking_trusted: "Affidabili e assicurati",
    booking_trusted_desc: "Professionisti verificati su cui può contare",
    booking_get_started: "Inizia ora",
    booking_skip: "Salta",
    booking_select_vehicle_type: "Selezioni il tipo di veicolo",
    booking_car_wash: "Lavaggio auto",
    booking_car_types: "Auto • SUV • Pickup • Furgoni",
    booking_boat_detail: "Detailing imbarcazione",
    booking_boat_types: "Yacht • Pontoni • Moto d'acqua",
    booking_rv_wash: "Lavaggio RV",
    booking_rv_types: "Camper • Roulotte • Rimorchi",
    booking_insured_vendors: "Fornitori assicurati",
    booking_5_star: "Servizio 5 stelle",
    booking_same_day: "Disponibile in giornata",
    booking_book_a_wash: "Prenota un lavaggio",
    booking_confirm_location: "Conferma posizione",
    booking_where_vehicle: "Dove si trova il veicolo?",
    booking_current_location: "Utilizzeremo la sua posizione attuale",
    booking_use_current: "Usa posizione attuale",
    booking_or: "OPPURE",
    booking_enter_address: "Inserisci indirizzo manualmente",
    booking_address_placeholder: "Inserisci via, città, provincia...",
    booking_location_confirmed: "Posizione confermata",
    booking_choose_service: "Scelga il servizio",
    booking_from_quick: "Dal lavaggio rapido alla pulizia completa",
    booking_most_popular: "Più richiesto",
    booking_best_value: "Miglior rapporto qualità-prezzo",
    booking_premium: "Premium",
    booking_estimated_time: "Stimato",
    booking_select_vehicle_title: "Seleziona il veicolo",
    booking_add_vehicle: "Aggiungi il suo veicolo",
    booking_no_vehicles: "Nessun veicolo presente",
    booking_add_first_vehicle: "Aggiunga il suo primo veicolo per iniziare",
    booking_vehicle_make: "Marca",
    booking_vehicle_model: "Modello",
    booking_vehicle_year: "Anno",
    booking_vehicle_color: "Colore",
    booking_save_vehicle: "Salva veicolo",
    booking_schedule_title: "Pianifica il lavaggio",
    booking_select_preferred: "Selezioni la data e l'orario preferiti",
    booking_morning: "Mattina",
    booking_afternoon: "Pomeriggio",
    booking_evening: "Sera",
    booking_checkout_title: "Riepilogo ordine",
    booking_review_confirm: "Riveda e confermi la prenotazione",
    booking_order_summary: "Riepilogo ordine",
    booking_promo_code: "Ha un codice promozionale?",
    booking_apply: "Applica",
    booking_vip_save: "I membri VIP risparmiano il 10%",
    booking_join_vip: "Diventa VIP",
    booking_pay_with_card: "Paga con carta",
    booking_processing: "Elaborazione pagamento...",
    booking_finding_washer: "Ricerca del suo WashPro",
    booking_matching: "Stiamo abbinandola al miglior operatore nella sua zona",
    booking_booking_confirmed: "Prenotazione confermata!",
    booking_washer_assigned: "Il suo WashPro è in arrivo",
    booking_eta: "Arrivo stimato",
    booking_contact_washer: "Contatta l'operatore",
    booking_track_location: "Traccia posizione",
    booking_vip_exclusive: "Offerta esclusiva VIP",
    booking_vip_save_10: "Risparmia il 10% su ogni lavaggio",
    booking_vip_month: "/mese",
    booking_vip_priority: "Prenotazione prioritaria",
    booking_vip_support: "Assistenza premium 24/7",
    booking_vip_cancel: "Disdici in qualsiasi momento",
    booking_become_vip: "Diventa membro VIP",
    booking_no_thanks: "No, grazie",
    
    // Vendor Portal
    vendor_dashboard: "Dashboard",
    vendor_jobs: "Lavori",
    vendor_pricing: "Tariffe",
    vendor_rankings: "Classifiche",
    vendor_insurance: "Assicurazione",
    vendor_earnings: "Guadagni",
    vendor_settings: "Impostazioni",
    vendor_available: "Disponibile per lavori",
    vendor_unavailable: "Non disponibile",
    vendor_new_job: "Nuova richiesta di lavoro",
    vendor_accept: "Accetta",
    vendor_decline: "Rifiuta",
    vendor_in_progress: "In corso",
    vendor_completed: "Completato",
    vendor_total_earnings: "Guadagni totali",
    vendor_this_week: "Questa settimana",
    vendor_this_month: "Questo mese",
    
    // Common Actions
    action_save: "Salva",
    action_cancel: "Annulla",
    action_edit: "Modifica",
    action_delete: "Elimina",
    action_confirm: "Conferma",
    action_close: "Chiudi",
    action_loading: "Caricamento...",
    action_submitting: "Invio in corso...",
    action_success: "Operazione riuscita!",
    action_error: "Errore",
    action_retry: "Riprova",
  },
  ru: {
    // Header
    nav_services: "Услуги",
    nav_how_it_works: "Как это работает",
    nav_vendors: "Для мастеров",
    nav_pricing: "Цены",
    nav_login: "Войти",
    nav_get_started: "Начать",
    
    // Hero Section
    hero_tagline: "WAX AND SHINE HEADQUARTERS™",
    hero_subtitle: "THE CARWASH CONCIERGE COMPANY™",
    hero_title_1: "Мобильная мойка",
    hero_title_2: "премиум-класса",
    hero_title_3: "по запросу",
    hero_description: "Будущее ухода за автомобилем уже здесь. Профессиональный детейлинг с выездом к вам — от проверенных и застрахованных мастеров.",
    hero_cta: "Записаться на мойку",
    hero_watch_demo: "Смотреть демо",
    hero_feature_1: "Мастера с рейтингом 5★",
    hero_feature_2: "Проверены и застрахованы",
    hero_feature_3: "Выезд по запросу",
    
    // Services Section
    services_title: "Наши услуги",
    services_subtitle: "Уход за любым транспортным средством",
    service_car_wash: "Премиум-мойка",
    service_car_wash_desc: "Профессиональный детейлинг с выездом к вам",
    service_boat: "Детейлинг катеров",
    service_boat_desc: "Морская чистка любых плавсредств",
    service_rv: "Мойка автодомов",
    service_rv_desc: "Полный уход за автодомами и кемперами",
    service_fleet: "Корпоративный автопарк",
    service_fleet_desc: "Мойка коммерческого транспорта",
    
    // How It Works
    how_it_works_title: "Как это работает",
    how_it_works_subtitle: "Сверкающий автомобиль за 3 простых шага",
    step_1_title: "Запишитесь онлайн",
    step_1_desc: "Выберите услугу, тип авто и удобное время",
    step_2_title: "Мастер приедет к вам",
    step_2_desc: "Проверенный мастер прибудет по вашему адресу",
    step_3_title: "Наслаждайтесь блеском",
    step_3_desc: "Отдыхайте, пока мы преображаем ваш автомобиль",
    
    // For Vendors
    vendors_title: "Для мастеров",
    vendors_subtitle: "Присоединяйтесь к сети профессиональных детейлеров",
    vendors_benefit_1: "Сами выбираете график",
    vendors_benefit_1_desc: "Работайте когда и где удобно",
    vendors_benefit_2: "Развивайте бизнес",
    vendors_benefit_2_desc: "Тысячи клиентов в вашем районе",
    vendors_benefit_3: "Быстрые выплаты",
    vendors_benefit_3_desc: "Еженедельные выплаты прямо на счёт",
    vendors_cta: "Стать мастером",
    vendors_pricing: "Посмотреть цены",
    vendors_watch_tutorial: "Смотреть обучающее видео",
    
    // Pricing Section
    pricing_title: "Прайс-лист",
    pricing_subtitle: "Стартовые цены для всех типов транспорта. Мастера устанавливают свои тарифы — найдите лучшую цену для вас.",
    pricing_car: "Легковой автомобиль",
    pricing_car_types: "Купе, седаны, хэтчбеки",
    pricing_suv: "Внедорожник / Пикап",
    pricing_suv_types: "Внедорожники, пикапы, минивэны",
    pricing_boat: "Катера и лодки",
    pricing_boat_types: "Все типы плавсредств",
    pricing_rv: "Автодома",
    pricing_rv_types: "Моторхоумы и кемперы",
    pricing_starting: "От",
    pricing_per_ft: "/фут свыше",
    
    // Add-ons
    addons_title: "Популярные дополнительные услуги",
    addon_vacuum: "Пылесос салона",
    addon_leather: "Уход за кожей",
    addon_wax: "Восковое покрытие",
    addon_pet_hair: "Удаление шерсти животных",
    pricing_disclaimer: "*Указаны стартовые цены. Мастера устанавливают свои тарифы. Ко всем заказам применяется комиссия сети 20%.",
    
    // Vendor Pricing Note
    vendor_pricing_title: "Мастера сами устанавливают цены",
    vendor_pricing_desc: "Каждый мастер настраивает свой прайс. Сравнивайте мастеров и находите лучшие тарифы в вашем районе.",
    vendor_pricing_tip: "Совет: конкурентные цены привлекают больше клиентов — если тариф слишком высокий, клиент выберет более доступный вариант.",
    vendor_pricing_cta: "Панель управления ценами",
    
    // Booking Policy
    policy_title: "Политика бронирования и отмены",
    policy_arrival_title: "Требования к прибытию мастера",
    policy_arrival_1: "Мастер должен прибыть в течение 30 минут от назначенного времени",
    policy_arrival_2: "При задержке мастер обязан регулярно информировать клиента о статусе",
    policy_arrival_3: "Отслеживание в реальном времени доступно после выезда мастера",
    policy_cancel_title: "Политика отмены для клиентов",
    policy_cancel_free: "Бесплатная отмена до принятия заказа мастером",
    policy_cancel_fee: "Комиссия 20% при отмене после выезда мастера или в течение 20 минут после принятия заказа",
    policy_cancel_brick: "Те же условия действуют для записи в стационарные мойки",
    policy_why: "Почему такая политика?",
    policy_why_desc: "Мы ценим время и мастеров, и клиентов. Политика защищает мастеров от холостых выездов и лишних расходов, а клиентов мотивирует соблюдать договорённости.",
    
    // Membership
    membership_title: "Членство для клиентов",
    membership_subtitle: "Бесплатно или VIP — выбирайте свои привилегии",
    membership_free: "Бесплатно",
    membership_free_desc: "Для редких моек",
    membership_vip: "VIP-участник",
    membership_vip_desc: "Для тех, кто следит за авто",
    membership_feature_booking: "Удобное онлайн-бронирование",
    membership_feature_vendors: "Доступ ко всем исполнителям",
    membership_feature_tracking: "Отслеживание в реальном времени",
    membership_feature_support: "Поддержка клиентов",
    membership_feature_priority: "Приоритетное бронирование",
    membership_feature_discount: "Скидка 10% на все услуги",
    membership_feature_rewards: "Эксклюзивные бонусы",
    membership_feature_deals: "Ранний доступ к акциям",
    membership_cta_free: "Начать бесплатно",
    membership_cta_vip: "Стать VIP",
    
    // Footer
    footer_pay_per_wash: "Платите только за мойку",
    footer_secure: "Безопасные платежи",
    footer_platform_fee: "Комиссия платформы:",
    footer_vip_video: "VIP-преимущества в действии",
    
    // Portal Selection
    portal_title: "ВЫБЕРИТЕ ПОРТАЛ",
    portal_customer: "Клиент",
    portal_vendor: "Исполнитель",
    portal_admin: "Администратор",
    
    // Common
    learn_more: "Подробнее",
    contact_us: "Связаться с нами",
    terms: "Условия использования",
    privacy: "Политика конфиденциальности",
    per_month: "/мес",
    recommended: "Рекомендуем",
    
    // Service History & Recurring
    service_history: "История услуг",
    past_bookings: "Прошлые записи",
    recurring_bookings: "Регулярные записи",
    no_bookings: "Записей пока нет",
    rebook_service: "Повторить запись",
    make_recurring: "Сделать регулярной",
    add_tip: "Добавить чаевые",
    tip_added: "Чаевые добавлены",
    every_x_days: "Каждые {days} дн.",
    pause: "Приостановить",
    resume: "Возобновить",
    cancel_recurring: "Отменить регулярную",
    
    // Tip Collection
    add_a_tip: "Оставить чаевые",
    show_appreciation: "Отблагодарите исполнителя",
    tip_goes_to_vendor: "100% чаевых уходит напрямую",
    send_tip: "Отправить чаевые",
    skip_for_now: "Пропустить",
    thank_you: "Спасибо!",
    tip_sent: "Чаевые отправлены",
    
    // Fleet Contact
    fleet_management: "Управление автопарком",
    get_custom_pricing: "Индивидуальные условия для бизнеса",
    company_info: "Данные компании",
    company_name: "Название компании",
    contact_name: "Контактное лицо",
    fleet_details: "Детали автопарка",
    vehicle_types: "Типы транспортных средств",
    fleet_size: "Размер автопарка",
    service_frequency: "Желаемая частота обслуживания",
    submit_inquiry: "Отправить запрос",
    inquiry_submitted: "Запрос отправлен!",
    
    // Shop Finder
    find_shop: "Найти мойку",
    nearby_shops: "Автомойки рядом",
    book_appointment: "Записаться",
    select_date: "Выберите дату",
    select_time: "Выберите время",
    available_slots: "Доступные слоты",
    confirm_booking: "Подтвердить запись",
    
    // Admin Dashboard
    admin_dashboard: "Панель администратора",
    admin_master_access: "Главный доступ администратора",
    admin_full_control: "Полный контроль над платформой WashPros",
    admin_service_map: "Карта услуг",
    admin_crm: "CRM",
    admin_revenue: "Выручка",
    admin_pricing: "Ценообразование",
    admin_surge_config: "Настройка надбавок",
    admin_surge_analytics: "Аналитика надбавок",
    admin_leaderboard: "Рейтинг",
    admin_insurance: "Страхование",
    admin_agreements: "Договоры",
    admin_video_center: "Видеоцентр",
    admin_vendor_analytics: "Аналитика исполнителей",
    admin_vendors: "Исполнители",
    admin_customers: "Клиенты",
    admin_bookings: "Заказы",
    admin_blog: "Блог",
    admin_social_media: "Соцсети",
    admin_notifications: "Уведомления",
    admin_settings: "Настройки",
    
    // Admin Stats
    admin_total_customers: "Всего клиентов",
    admin_total_vendors: "Всего исполнителей",
    admin_pending: "в ожидании",
    admin_total_bookings: "Всего заказов",
    admin_monthly_revenue: "Выручка за месяц",
    admin_active_bookings: "Активные заказы",
    admin_completed_today: "Выполнено сегодня",
    admin_pending_approval: "Ожидают подтверждения",
    admin_cancelled: "Отменённые",
    
    // Admin Actions
    admin_view_all: "Смотреть все",
    admin_approve: "Одобрить",
    admin_reject: "Отклонить",
    admin_details: "Подробнее",
    admin_edit: "Изменить",
    admin_delete: "Удалить",
    admin_save: "Сохранить",
    admin_cancel: "Отмена",
    admin_search: "Поиск",
    admin_filter: "Фильтр",
    admin_export: "Экспорт",
    admin_refresh: "Обновить",
    
    // Admin Sections
    admin_quick_actions: "Быстрые действия",
    admin_recent_activity: "Последние действия",
    admin_pending_vendors: "Исполнители на проверке",
    admin_recent_bookings: "Последние заказы",
    admin_platform_settings: "Настройки платформы",
    admin_platform_name: "Название платформы",
    admin_support_email: "Email поддержки",
    admin_platform_fee: "Комиссия платформы",
    admin_vip_membership: "Цена VIP-подписки",
    admin_changes_saved: "Изменения сохранены",
    admin_save_changes: "Сохранить изменения",
    admin_cancel_changes: "Отменить изменения",
    admin_unsaved_changes: "Есть несохранённые изменения",
    
    // Video Center
    admin_video_gallery: "Видеогалерея",
    admin_video_downloads: "Загрузки видео",
    admin_generate_video: "Создать видео",
    admin_video_status: "Статус видео",
    admin_generating: "Создаётся...",
    admin_completed: "Завершено",
    admin_failed: "Ошибка",
    
    // CRM
    admin_customer_management: "Управление клиентами",
    admin_vendor_management: "Управление исполнителями",
    admin_total_revenue: "Общая выручка",
    admin_average_order: "Средний чек",
    admin_active_users: "Активные пользователи",
    admin_vip_members: "VIP-участники",
    admin_basic_tier: "Базовый уровень",
    admin_pro_tier: "Про-уровень",
    admin_elite_tier: "Элитный уровень",
    
    // Map
    admin_all_locations: "Все локации",
    admin_customers_only: "Только клиенты",
    admin_vendors_only: "Только исполнители",
    admin_map_legend: "Легенда карты",
    admin_basic_customer: "Базовый клиент",
    admin_vip_customer: "VIP-клиент",
    admin_basic_vendor: "Базовый исполнитель",
    admin_pro_vendor: "Про-исполнитель",
    admin_elite_vendor: "Элитный исполнитель",
    admin_mobile_vendor: "Мобильный исполнитель",
    admin_shop_vendor: "Стационарная точка",
    
    // Insurance
    admin_insurance_verification: "Проверка страховки",
    admin_verify_documents: "Проверить документы",
    admin_document_status: "Статус документов",
    admin_verified: "Проверено",
    admin_pending_review: "На проверке",
    admin_expired: "Истёк срок",
    
    // Notifications
    admin_notification_analytics: "Аналитика уведомлений",
    admin_sent_notifications: "Отправленные уведомления",
    admin_delivery_rate: "Доставляемость",
    admin_open_rate: "Открываемость",
    
    // Customer Dashboard
    dashboard_welcome: "С возвращением,",
    dashboard_home: "Главная",
    dashboard_discover: "Обзор",
    dashboard_rewards: "Бонусы",
    dashboard_history: "История",
    dashboard_inbox: "Сообщения",
    dashboard_profile: "Профиль",
    dashboard_current_location: "Текущее местоположение",
    dashboard_what_needs_washing: "Что будем мыть?",
    dashboard_quick_book: "Быстрая запись",
    dashboard_book_now: "Записаться",
    dashboard_popular_services: "Популярные услуги",
    dashboard_featured_vendors: "Рекомендуемые мастера",
    dashboard_my_vehicles: "Мои автомобили",
    dashboard_add_vehicle: "Добавить",
    dashboard_no_vehicles: "Автомобили ещё не добавлены",
    dashboard_add_first_vehicle: "Добавьте первый автомобиль",
    dashboard_find_vendors: "Найти мастеров",
    dashboard_see_all: "Все",
    dashboard_see_how_works: "Как это работает",
    dashboard_earn_referral: "Получайте $10 за каждого друга",
    dashboard_invite_friends: "Приглашайте друзей и зарабатывайте",
    dashboard_messages: "Сообщения",
    dashboard_logout: "Выйти",
    dashboard_profile_settings: "Настройки профиля",
    dashboard_favorites: "Избранное",
    
    // Vehicle Types
    vehicle_car: "Автомобиль",
    vehicle_boat: "Лодка",
    vehicle_rv: "Автодом",
    vehicle_fleet: "Автопарк",
    vehicle_golf_cart: "Гольф-кар",
    vehicle_recreational: "Внедорожная техника",
    vehicle_luxury: "Премиум",
    
    // Booking Flow
    booking_select_date: "Выберите дату",
    booking_select_time: "Выберите время",
    booking_location: "Адрес обслуживания",
    booking_use_my_location: "Использовать моё местоположение",
    booking_search_address: "Поиск адреса",
    booking_special_instructions: "Особые пожелания",
    booking_select_vehicle: "Выберите автомобиль",
    booking_choose_package: "Выберите пакет",
    booking_add_extras: "Дополнительные услуги",
    booking_review_order: "Проверка и оплата",
    booking_payment: "Оплата",
    booking_finding_vendor: "Ищем вашего WashPro...",
    booking_vendor_assigned: "Мастер назначен",
    booking_continue: "Далее",
    booking_back: "Назад",
    booking_confirm_booking: "Подтвердить запись",
    booking_pay_now: "Оплатить",
    booking_subtotal: "Сумма",
    booking_service_fee: "Сервисный сбор",
    booking_tip: "Чаевые",
    booking_total: "Итого",
    booking_add_tip: "Добавить чаевые",
    booking_no_tip: "Без чаевых",
    booking_custom: "Своя сумма",
    
    // Service Packages
    package_basic: "Базовая мойка",
    package_deluxe: "Расширенная мойка",
    package_premium: "Премиум-детейлинг",
    package_duration: "мин",
    
    // New Booking Flow Translations
    booking_premium_mobile: "Премиум мобильный детейлинг",
    booking_on_demand: "Мойка по запросу",
    booking_on_demand_desc: "Профессиональная мойка в любое удобное время",
    booking_we_come: "Приедем к вам",
    booking_we_come_desc: "Обслуживание дома, в офисе или в любом другом месте",
    booking_trusted: "Надёжно и застраховано",
    booking_trusted_desc: "Проверенные профессионалы, которым можно доверять",
    booking_get_started: "Начать",
    booking_skip: "Пропустить",
    booking_select_vehicle_type: "Выберите тип транспортного средства",
    booking_car_wash: "Мойка автомобиля",
    booking_car_types: "Легковые • Внедорожники • Пикапы • Минивэны",
    booking_boat_detail: "Детейлинг лодки",
    booking_boat_types: "Яхты • Понтоны • Гидроциклы",
    booking_rv_wash: "Мойка автодома",
    booking_rv_types: "Моторхоумы • Кемперы • Прицепы",
    booking_insured_vendors: "Застрахованные мастера",
    booking_5_star: "Сервис 5 звёзд",
    booking_same_day: "Доступно в тот же день",
    booking_book_a_wash: "Записаться на мойку",
    booking_confirm_location: "Подтвердить адрес",
    booking_where_vehicle: "Где находится автомобиль?",
    booking_current_location: "Используем ваше текущее местоположение",
    booking_use_current: "Моё местоположение",
    booking_or: "ИЛИ",
    booking_enter_address: "Ввести адрес вручную",
    booking_address_placeholder: "Улица, город, регион...",
    booking_location_confirmed: "Адрес подтверждён",
    booking_choose_service: "Выберите услугу",
    booking_from_quick: "От быстрой мойки до полного детейлинга",
    booking_most_popular: "Хит",
    booking_best_value: "Выгодно",
    booking_premium: "Премиум",
    booking_estimated_time: "Примерно",
    booking_select_vehicle_title: "Выберите автомобиль",
    booking_add_vehicle: "Добавить автомобиль",
    booking_no_vehicles: "Нет автомобилей",
    booking_add_first_vehicle: "Добавьте первый автомобиль, чтобы начать",
    booking_vehicle_make: "Марка",
    booking_vehicle_model: "Модель",
    booking_vehicle_year: "Год",
    booking_vehicle_color: "Цвет",
    booking_save_vehicle: "Сохранить",
    booking_schedule_title: "Запись на мойку",
    booking_select_preferred: "Выберите удобные дату и время",
    booking_morning: "Утро",
    booking_afternoon: "День",
    booking_evening: "Вечер",
    booking_checkout_title: "Оформление",
    booking_review_confirm: "Проверьте и подтвердите заказ",
    booking_order_summary: "Состав заказа",
    booking_promo_code: "Есть промокод?",
    booking_apply: "Применить",
    booking_vip_save: "VIP-участники экономят 10%",
    booking_join_vip: "Стать VIP",
    booking_pay_with_card: "Оплатить картой",
    booking_processing: "Обрабатываем платёж...",
    booking_finding_washer: "Ищем вашего WashPro",
    booking_matching: "Подбираем лучшего мастера рядом с вами",
    booking_booking_confirmed: "Заказ подтверждён!",
    booking_washer_assigned: "Ваш WashPro уже едет",
    booking_eta: "Прибудет примерно через",
    booking_contact_washer: "Связаться с мастером",
    booking_track_location: "Отследить",
    booking_vip_exclusive: "Только для VIP",
    booking_vip_save_10: "Скидка 10% на каждую мойку",
    booking_vip_month: "/мес",
    booking_vip_priority: "Приоритетная запись",
    booking_vip_support: "Поддержка 24/7",
    booking_vip_cancel: "Отмена в любой момент",
    booking_become_vip: "Стать VIP-участником",
    booking_no_thanks: "Не сейчас",
    
    // Vendor Portal
    vendor_dashboard: "Главная",
    vendor_jobs: "Заказы",
    vendor_pricing: "Цены",
    vendor_rankings: "Рейтинг",
    vendor_insurance: "Страховка",
    vendor_earnings: "Доходы",
    vendor_settings: "Настройки",
    vendor_available: "Принимаю заказы",
    vendor_unavailable: "Недоступен",
    vendor_new_job: "Новый заказ",
    vendor_accept: "Принять",
    vendor_decline: "Отклонить",
    vendor_in_progress: "В работе",
    vendor_completed: "Выполнено",
    vendor_total_earnings: "Всего заработано",
    vendor_this_week: "За неделю",
    vendor_this_month: "За месяц",
    
    // Common Actions
    action_save: "Сохранить",
    action_cancel: "Отмена",
    action_edit: "Изменить",
    action_delete: "Удалить",
    action_confirm: "Подтвердить",
    action_close: "Закрыть",
    action_loading: "Загрузка...",
    action_submitting: "Отправляем...",
    action_success: "Готово!",
    action_error: "Ошибка",
    action_retry: "Повторить",
  },
  // Phase i18n expansion 2026-06-12 — Haitian Creole (new locale).
  ht: {
    "nav_services": "Sèvis",
    "nav_how_it_works": "Kòman Li Fonksyone",
    "nav_vendors": "Pou Vandè yo",
    "nav_pricing": "Pri a",
    "nav_login": "Konekte",
    "nav_get_started": "Kòmanse",
    "hero_tagline": "CHÈF BIWO WAX AK BRILYE™",
    "hero_subtitle": "KONPAYI KONSIYÈJ LAVAY MACHI™",
    "hero_title_1": "Lavaj Machin Mobil",
    "hero_title_2": "Prim",
    "hero_title_3": "Sou Demann",
    "hero_description": "Eksperyans avni swen machin. Sèvis detaye pwofesyonèl delivre nan papòt ou pa vandè verifye, asire.",
    "hero_cta": "Rezève Lavaj ou",
    "hero_watch_demo": "Gade Demo a",
    "hero_feature_1": "Vandè 5-Etòl",
    "hero_feature_2": "Asire & Verifye",
    "hero_feature_3": "Sèvis Sou Demann",
    "services_title": "Sèvis Nou Yo",
    "services_subtitle": "Solisyon swen machin prim pou chak kalite veyikil",
    "service_car_wash": "Lavaj Machin Prim",
    "service_car_wash_desc": "Detay mobil pwofesyonèl dirèkteman lakay ou",
    "service_boat": "Detaye Bato",
    "service_boat_desc": "Nètwayaj degre maren pou tout bato",
    "service_rv": "Lavaj RV",
    "service_rv_desc": "Nètwayaj konplè pou RV ak lakay mobil",
    "service_fleet": "Jesyon Flòt",
    "service_fleet_desc": "Solisyon pou lave flòt komèsyal yo",
    "how_it_works_title": "Kòman Li Fonksyone",
    "how_it_works_subtitle": "Fè veyikil ou briye nan 3 etap fasil",
    "step_1_title": "Rezève Sou Entènèt",
    "step_1_desc": "Chwazi sèvis ou, veyikil ou, ak lè preferans ou",
    "step_2_title": "Nou Vin Kote Ou",
    "step_2_desc": "Vandè verifye nou an rive nan kote ou",
    "step_3_title": "Jwi Briye a",
    "step_3_desc": "Rilaks pandan nap transfòme veyikil ou",
    "vendors_title": "Pou Vandè yo",
    "vendors_subtitle": "Antre nan rezo nou an kòm detaye pwofesyonèl",
    "vendors_benefit_1": "Fè Pwòp Orè Ou",
    "vendors_benefit_1_desc": "Travay lè ou vle, kote ou vle",
    "vendors_benefit_2": "Kwasans Biznis ou",
    "vendors_benefit_2_desc": "Aksè a dè milye de kliyan nan zòn ou",
    "vendors_benefit_3": "Peyman Rapid",
    "vendors_benefit_3_desc": "Peman chak semèn dirèkteman nan kont ou",
    "vendors_cta": "Vin yon Vandè",
    "vendors_pricing": "Wè Pri Sèvis yo",
    "vendors_watch_tutorial": "Gade Videyo Tutorial",
    "pricing_title": "Gid Pri Sèvis yo",
    "pricing_subtitle": "Pri kòmanse pou tout kalite veyikil yo. Vandè yo fikse tarif yo - jwenn pi bon pri pou bezwen ou.",
    "pricing_car": "Machin / Sedan",
    "pricing_car_types": "Coupé, Sedan, Hatchback",
    "pricing_suv": "SUV / Kamyon",
    "pricing_suv_types": "SUV, Kamyon, Furgonn",
    "pricing_boat": "Bato",
    "pricing_boat_types": "Tout Kalite Bato",
    "pricing_rv": "Karyavan",
    "pricing_rv_types": "Kay Kamyon & Kanmpè",
    "pricing_starting": "Kòmanse avèk",
    "pricing_per_ft": "/pye pou chak sou",
    "addons_title": "Sèvis Siplemantè Popilè",
    "addon_vacuum": "Aspirasyon Entèn",
    "addon_leather": "Swen Pou Po Bwèt",
    "addon_wax": "Poze Sir",
    "addon_pet_hair": "Netwaye Chèvwa Zanimo",
    "pricing_disclaimer": "*Pri kòmanse yo montre. Vandè yo fikse pwòp to yo. Yon Frè Sèvis Rezo 20% aplike pou tout randevou yo.",
    "vendor_pricing_title": "Vandè yo Fikse Pwòp Pri yo",
    "vendor_pricing_desc": "Chak vandè ka personnaliser pri yo. Konpare vandè yo pou jwenn pi bon pri nan zòn ou an.",
    "vendor_pricing_tip": "Konsèy Pro: Pri konpetitif atire plis kliyan - si pri yo twò wo, kliyan yo ka chwazi yon opsyon ki pi abòdab.",
    "vendor_pricing_cta": "Tablo Pri Vandè yo",
    "policy_title": "Regleman Rezèvasyon & Anilasyon",
    "policy_arrival_title": "Espètasyon Pou Arive Vandè yo",
    "policy_arrival_1": "Vandè yo dwe rive nan 30 minit apre lè randevou ki pwograme a",
    "policy_arrival_2": "Si yo an reta, yo oblije bay mizajou estati kontinyèl bay kliyan an",
    "policy_arrival_3": "Swivi an tan reyèl disponib lè vandè a sou wout",
    "policy_cancel_title": "Regleman Anilasyon Kliyan",
    "policy_cancel_free": "Anilasyon gratis anvan vandè aksepte rezèvasyon an",
    "policy_cancel_fee": "Frè anilasyon 20% si anile apre vandè a sou wout oswa nan 20 minit apre rezèvasyon aksepte",
    "policy_cancel_brick": "Menm regleman an aplike pou randevou nan boutik fizik yo",
    "policy_why": "Poukisa regleman sa a?",
    "policy_why_desc": "Nou apresye tan yo tou de vandè ak kliyan nou yo. Regleman sa a pwoteje vandè yo kont deplasman san nesesite ak pri gaz pandan li ankouraje kliyan yo pou respekte randevou yo.",
    "membership_title": "Abònman Kliyan",
    "membership_subtitle": "Antre gratis oswa amelyore nan VIP pou plis avantaj",
    "membership_free": "Gratis",
    "membership_free_desc": "Parèt pou netwayaj okazyonèl",
    "membership_vip": "Manm VIP",
    "membership_vip_desc": "Pou amater swen machin",
    "membership_feature_booking": "Rezèvasyon sou entènèt fasil",
    "membership_feature_vendors": "Akses a tout vandè yo",
    "membership_feature_tracking": "Swivi an tan reyèl",
    "membership_feature_support": "Sipò pou kliyan",
    "membership_feature_priority": "Rezèvasyon priyorite",
    "membership_feature_discount": "10% rabè sou tout sèvis yo",
    "membership_feature_rewards": "Rekonpans eksklizif",
    "membership_feature_deals": "Akses bonè pou kontra",
    "membership_cta_free": "Kòmanse Gratis",
    "membership_cta_vip": "Antre VIP",
    "footer_pay_per_wash": "Peye sèlman lè ou fè netwayaj",
    "footer_secure": "Peman sekirize",
    "footer_platform_fee": "Frè platfòm:",
    "footer_vip_video": "Gade Avantaj VIP an Aksyon",
    "portal_title": "CHWAZI PORTAL OU",
    "portal_customer": "Kliyan",
    "portal_vendor": "Founisè",
    "portal_admin": "Admin",
    "learn_more": "Aprann Plis",
    "contact_us": "Kontakte Nou",
    "terms": "Kondisyon Sèvis",
    "privacy": "Règleman sou Konfidansyalite",
    "per_month": "/mwa",
    "recommended": "Rekòmande",
    "service_history": "Istwa Sèvis",
    "past_bookings": "Ansyen Rezèvasyon ou yo",
    "recurring_bookings": "Rezèvasyon ki repete yo",
    "no_bookings": "Pa gen rezèvasyon ankò",
    "rebook_service": "Rezève Sèvis sa a Ankò",
    "make_recurring": "Fè li Repete",
    "add_tip": "Ajoute Poubo",
    "tip_added": "Poubo Ajoute",
    "every_x_days": "Chak {days} jou",
    "pause": "Pause",
    "resume": "Kontinye",
    "cancel_recurring": "Anile Repete",
    "add_a_tip": "Ajoute yon Poubo",
    "show_appreciation": "Montre Rekonesans ou",
    "tip_goes_to_vendor": "100% nan poubo ou ale dirèkteman bay",
    "send_tip": "Voye Poubo",
    "skip_for_now": "Sote pou kounye a",
    "thank_you": "Mèsi!",
    "tip_sent": "Ou voye poubo a",
    "fleet_management": "Jesyon Fwot",
    "get_custom_pricing": "Jwenn pri pèsonalize pou biznis ou",
    "company_info": "Enfòmasyon Konpayi",
    "company_name": "Non Konpayi an",
    "contact_name": "Non Kontak",
    "fleet_details": "Detay Fwot",
    "vehicle_types": "Kalite Machin nan Fwot la",
    "fleet_size": "Gwosè Fwot",
    "service_frequency": "Frekans Sèvis Desirab",
    "submit_inquiry": "Soumèt Demann Fwot",
    "inquiry_submitted": "Demann Soumèt!",
    "find_shop": "Jwenn yon Boutik",
    "nearby_shops": "Boutik Lave Machin ki toupre yo",
    "book_appointment": "Pran Randevou",
    "select_date": "Chwazi Dat",
    "select_time": "Chwazi Lè",
    "available_slots": "Disponibilite",
    "confirm_booking": "Konfime Rezèvasyon",
    "admin_dashboard": "Panèl Admin",
    "admin_master_access": "Aksè Mèt Admin",
    "admin_full_control": "Kontwòl konplè sou platfòm WashPros",
    "admin_service_map": "Kat Sèvis",
    "admin_crm": "CRM",
    "admin_revenue": "Revenn",
    "admin_pricing": "Pri",
    "admin_surge_config": "Konfigirasyon Variasyon",
    "admin_surge_analytics": "Analyse Variasyon",
    "admin_leaderboard": "Tablo Lidè",
    "admin_insurance": "Asirans",
    "admin_agreements": "Akò",
    "admin_video_center": "Sant Videyo",
    "admin_vendor_analytics": "Analyse Vandè",
    "admin_vendors": "Vandè yo",
    "admin_customers": "Kliyan yo",
    "admin_bookings": "Rezèrvasyon",
    "admin_blog": "Blog",
    "admin_social_media": "Rezo Sosyal",
    "admin_notifications": "Notifikasyon",
    "admin_settings": "Anviwònman",
    "admin_total_customers": "Total Kliyan",
    "admin_total_vendors": "Total Vandè",
    "admin_pending": "annatant",
    "admin_total_bookings": "Total Rezèrvasyon",
    "admin_monthly_revenue": "Revenn Mansyèl",
    "admin_active_bookings": "Rezèrvasyon Aktif",
    "admin_completed_today": "Ki Konplete Jodi a",
    "admin_pending_approval": "Annatant Apwobasyon",
    "admin_cancelled": "Anile",
    "admin_view_all": "Gade Tout",
    "admin_approve": "Apwouve",
    "admin_reject": "Rejte",
    "admin_details": "Detay",
    "admin_edit": "Edite",
    "admin_delete": "Efase",
    "admin_save": "Sove",
    "admin_cancel": "Anile",
    "admin_search": "Chèche",
    "admin_filter": "Filtre",
    "admin_export": "Ekspòte",
    "admin_refresh": "Rafrechi",
    "admin_quick_actions": "Aksyon Rapid",
    "admin_recent_activity": "Aktivite Resan",
    "admin_pending_vendors": "Vandè Annatant",
    "admin_recent_bookings": "Rezèrvasyon Resan",
    "admin_platform_settings": "Anviwònman Platfòm",
    "admin_platform_name": "Non Platfòm",
    "admin_support_email": "Imèl Sipò",
    "admin_platform_fee": "Frè Platfòm",
    "admin_vip_membership": "Pri Manm VIP",
    "admin_changes_saved": "Chanjman yo te sove avèk siksè",
    "admin_save_changes": "Sove Chanjman",
    "admin_cancel_changes": "Anile Chanjman yo",
    "admin_unsaved_changes": "Ou gen chanjman ki pako sove",
    "admin_video_gallery": "Galri Videyo",
    "admin_video_downloads": "Telechajman Videyo",
    "admin_generate_video": "Jenere Videyo",
    "admin_video_status": "Estati Videyo",
    "admin_generating": "Ap Jenere...",
    "admin_completed": "Fini",
    "admin_failed": "Echwe",
    "admin_customer_management": "Jesyon Kliyan",
    "admin_vendor_management": "Jesyon Vandè",
    "admin_total_revenue": "Revni Total",
    "admin_average_order": "Valè Mwayèn Kòmand",
    "admin_active_users": "Itilizatè Aktif",
    "admin_vip_members": "Manm VIP",
    "admin_basic_tier": "Nivo Debaz",
    "admin_pro_tier": "Nivo Pro",
    "admin_elite_tier": "Nivo Elit",
    "admin_all_locations": "Tout Lokalizasyon",
    "admin_customers_only": "Kliyan Sèlman",
    "admin_vendors_only": "Vandè Sèlman",
    "admin_map_legend": "Lejann Kat",
    "admin_basic_customer": "Kliyan Debaz",
    "admin_vip_customer": "Kliyan VIP",
    "admin_basic_vendor": "Vandè Debaz",
    "admin_pro_vendor": "Vandè Pro",
    "admin_elite_vendor": "Vandè Elit",
    "admin_mobile_vendor": "Vandè Mobil",
    "admin_shop_vendor": "Boutik/Fizik",
    "admin_insurance_verification": "Verifikasyon Asirans",
    "admin_verify_documents": "Verifye Dokiman",
    "admin_document_status": "Estati Dokiman",
    "admin_verified": "Verifye",
    "admin_pending_review": "An Atant Review",
    "admin_expired": "Ekspire",
    "admin_notification_analytics": "Analiz Notifikasyon",
    "admin_sent_notifications": "Notifikasyon Voye",
    "admin_delivery_rate": "To Livrezon",
    "admin_open_rate": "To Ouvèti",
    "dashboard_welcome": "Byenveni tounen,",
    "dashboard_home": "Akèy",
    "dashboard_discover": "Dekouvri",
    "dashboard_rewards": "Rekonpans",
    "dashboard_history": "Istwa",
    "dashboard_inbox": "Bwat Resepsyon",
    "dashboard_profile": "Profil",
    "dashboard_current_location": "Kote Kounye a",
    "dashboard_what_needs_washing": "Kisa ki bezwen lavé?",
    "dashboard_quick_book": "Rezèvasyon Rapid",
    "dashboard_book_now": "Rezève Kounye a",
    "dashboard_popular_services": "Sèvis Popilè",
    "dashboard_featured_vendors": "Vandè Rekòmande",
    "dashboard_my_vehicles": "Machin Mwen",
    "dashboard_add_vehicle": "Ajoute",
    "dashboard_no_vehicles": "Pa gen machin ajoute ankò",
    "dashboard_add_first_vehicle": "Ajoute Premye Machin Ou",
    "dashboard_find_vendors": "Chèche Vandè",
    "dashboard_see_all": "Wè tout",
    "dashboard_see_how_works": "Gade Kòman sa Travay",
    "dashboard_earn_referral": "Jwenn $10 pou Chak Referans",
    "dashboard_invite_friends": "Envite Zanmi & Jwenn",
    "dashboard_messages": "Mesaj",
    "dashboard_logout": "Dekonekte",
    "dashboard_profile_settings": "Anviwònman Pwofil",
    "dashboard_favorites": "Favori",
    "vehicle_car": "Machin",
    "vehicle_boat": "Bato",
    "vehicle_rv": "Kamping",
    "vehicle_fleet": "Flòt",
    "vehicle_golf_cart": "Charyo Golf",
    "vehicle_recreational": "Rekreyasyon",
    "vehicle_luxury": "Liks",
    "booking_select_date": "Chwazi Dat",
    "booking_select_time": "Chwazi Lè",
    "booking_location": "Kote Sèvis",
    "booking_use_my_location": "Sèvi ak Kote Mwen Kounye a",
    "booking_search_address": "Chèche Adrès",
    "booking_special_instructions": "Enstriksyon Espesyal",
    "booking_select_vehicle": "Chwazi Machin",
    "booking_choose_package": "Chwazi Pakè Ou",
    "booking_add_extras": "Ajoute Sèvis Siplemantè",
    "booking_review_order": "Revize & Peye",
    "booking_payment": "Peman",
    "booking_finding_vendor": "Ap Chèche WashPro ou...",
    "booking_vendor_assigned": "Vandè Asiyen",
    "booking_continue": "Kontinye",
    "booking_back": "Retounen",
    "booking_confirm_booking": "Konfime Rezèvasyon",
    "booking_pay_now": "Peye Kounye a",
    "booking_subtotal": "Soustotal",
    "booking_service_fee": "Frè Sèvis",
    "booking_tip": "Pouboire",
    "booking_total": "Total",
    "booking_add_tip": "Ajoute yon Pouboire",
    "booking_no_tip": "Pa gen Pouboire",
    "booking_custom": "Pèsonalize",
    "package_basic": "Netwayaj Debaz",
    "package_deluxe": "Netwayaj Deliks",
    "package_premium": "Detay Premye Klas",
    "package_duration": "min",
    "booking_premium_mobile": "Detay Premye Klas Mobil",
    "booking_on_demand": "Lavaj sou Demann",
    "booking_on_demand_desc": "Lavaj pwofesyonèl nenpòt lè ou bezwen li",
    "booking_we_come": "Nou Vin Lakay Ou",
    "booking_we_come_desc": "Sèvis lakay ou, nan biwo a, oswa nenpòt kote",
    "booking_trusted": "Konfyab & Asire",
    "booking_trusted_desc": "Pwofesyonèl verifye ou ka konte sou yo",
    "booking_get_started": "Kòmanse",
    "booking_skip": "Sote",
    "booking_select_vehicle_type": "Chwazi kalite machin ou anba anba",
    "booking_car_wash": "Lavaj Machin",
    "booking_car_types": "Machin • SUV • Kamyon • Minivan",
    "booking_boat_detail": "Detay Bato",
    "booking_boat_types": "Yat • Ponpon • Jet Skis",
    "booking_rv_wash": "Lavaj RV",
    "booking_rv_types": "Karavàn • Kanpe • Remòk",
    "booking_insured_vendors": "Vandè Asire",
    "booking_5_star": "Sèvis 5-Etoiles",
    "booking_same_day": "Disponib Menm Jou",
    "booking_book_a_wash": "Rezeve yon Lavaj",
    "booking_confirm_location": "Konfime Kote",
    "booking_where_vehicle": "Kote machin nan ye?",
    "booking_current_location": "N ap itilize kote aktyèl ou",
    "booking_use_current": "Sèvi ak Kote Aktyèl",
    "booking_or": "OSWA",
    "booking_enter_address": "Antre Adrès Manyèlman",
    "booking_address_placeholder": "Antre adrès lari, vil, eta...",
    "booking_location_confirmed": "Kote Konfime",
    "booking_choose_service": "Chwazi Sèvis Ou",
    "booking_from_quick": "Soti nan lave rapid pou rive nan an detay plen",
    "booking_most_popular": "Pito Plis",
    "booking_best_value": "Pi Bon Valè",
    "booking_premium": "Premium",
    "booking_estimated_time": "Estimate",
    "booking_select_vehicle_title": "Chwazi Machin ou",
    "booking_add_vehicle": "Ajoute Machin Ou",
    "booking_no_vehicles": "Pa gen machin ankò",
    "booking_add_first_vehicle": "Ajoute premye machin ou pou kòmanse",
    "booking_vehicle_make": "Mak",
    "booking_vehicle_model": "Modèl",
    "booking_vehicle_year": "Ane",
    "booking_vehicle_color": "Koulè",
    "booking_save_vehicle": "Sove Machin",
    "booking_schedule_title": "Orè Lavaj Ou",
    "booking_select_preferred": "Chwazi dat ak lè ou prefere a",
    "booking_morning": "Maten",
    "booking_afternoon": "Apremidi",
    "booking_evening": "Swa",
    "booking_checkout_title": "Kesye",
    "booking_review_confirm": "Revize epi konfime rezèvasyon ou",
    "booking_order_summary": "Rezime Kòmand",
    "booking_promo_code": "Gen yon kòd pwomosyon?",
    "booking_apply": "Aplike",
    "booking_vip_save": "Manm VIP yo ekonomize 10%",
    "booking_join_vip": "Antre nan VIP",
    "booking_pay_with_card": "Peye ak kat",
    "booking_processing": "Pwosesis Peman...",
    "booking_finding_washer": "N ap chèche WashPro ou a",
    "booking_matching": "Nou ap matche w ak pi bon leswa nan zòn ou an",
    "booking_booking_confirmed": "Rezèvasyon Konfime!",
    "booking_washer_assigned": "WashPro ou an sou wout la",
    "booking_eta": "Tan Arive Prevwa",
    "booking_contact_washer": "Kontakte Leswa",
    "booking_track_location": "Swiv Kote",
    "booking_vip_exclusive": "Ofè Ekzkliziv pou VIP",
    "booking_vip_save_10": "Ekonomize 10% sou chak lave",
    "booking_vip_month": "/mwa",
    "booking_vip_priority": "Rezèvasyon an priyorite",
    "booking_vip_support": "Sipò priyorite 24/7",
    "booking_vip_cancel": "Anile nenpòt lè",
    "booking_become_vip": "Vin Manm VIP",
    "booking_no_thanks": "Non, Mèsi",
    "vendor_dashboard": "Panel",
    "vendor_jobs": "Travay",
    "vendor_pricing": "Pri",
    "vendor_rankings": "Klasman",
    "vendor_insurance": "Asirans",
    "vendor_earnings": "Revni",
    "vendor_settings": "Anviwònman",
    "vendor_available": "Disponib pou Travay",
    "vendor_unavailable": "Endisponib",
    "vendor_new_job": "Nouvo Demann Travay",
    "vendor_accept": "Aksepte",
    "vendor_decline": "Refize",
    "vendor_in_progress": "An Kour",
    "vendor_completed": "Fini",
    "vendor_total_earnings": "Total Revni",
    "vendor_this_week": "Semèn Sa a",
    "vendor_this_month": "Mwa Sa a",
    "action_save": "Sove",
    "action_cancel": "Anile",
    "action_edit": "Edité",
    "action_delete": "Efase",
    "action_confirm": "Konfime",
    "action_close": "Fèmen",
    "action_loading": "Chaje...",
    "action_submitting": "Voye...",
    "action_success": "Siksè!",
    "action_error": "Erè",
    "action_retry": "Eseye ankò"
  },
};

const languageNames = {
  en: { name: "English", flag: "🇺🇸" },
  es: { name: "Español", flag: "🇪🇸" },
  fr: { name: "Français", flag: "🇫🇷" },
  pt: { name: "Português", flag: "🇧🇷" },
  de: { name: "Deutsch", flag: "🇩🇪" },
  zh: { name: "中文", flag: "🇨🇳" },
  ja: { name: "日本語", flag: "🇯🇵" },
  ko: { name: "한국어", flag: "🇰🇷" },
  it: { name: "Italiano", flag: "🇮🇹" },
  ru: { name: "Русский", flag: "🇷🇺" },
  ht: { name: "Kreyòl Ayisyen", flag: "🇭🇹" }
};

// Supported languages list (same as i18n.js)
// Phase 7g.15.84/85 — extended to include Italian + Russian
// Phase i18n expansion 2026-06-12 — extended to include Haitian Creole
const supportedLanguages = ['en', 'es', 'fr', 'pt', 'de', 'zh', 'ja', 'ko', 'it', 'ru', 'ht'];

// Helper to detect browser language
const detectBrowserLanguage = () => {
  // Try navigator.language first (e.g., "en-US", "es-MX")
  const primaryLang = navigator.language?.split("-")[0];
  if (primaryLang && supportedLanguages.includes(primaryLang)) {
    return primaryLang;
  }
  
  // Try navigator.languages array (user's preferred languages)
  if (navigator.languages && navigator.languages.length > 0) {
    for (const lang of navigator.languages) {
      const langCode = lang.split("-")[0];
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }
  }
  
  // Fall back to English
  return "en";
};

// Check if this is the user's first visit
const isFirstVisit = () => {
  return !localStorage.getItem("washpros-language") && 
         !localStorage.getItem("washpros_language") &&
         !localStorage.getItem("washpros-visited");
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage (same key as i18n.js for consistency)
    const saved = localStorage.getItem("washpros-language");
    if (saved && supportedLanguages.includes(saved)) return saved;
    
    // Fall back to old key
    const oldSaved = localStorage.getItem("washpros_language");
    if (oldSaved && supportedLanguages.includes(oldSaved)) return oldSaved;
    
    // Auto-detect browser language for first-time visitors
    return detectBrowserLanguage();
  });
  
  const [autoDetected, setAutoDetected] = useState(false);
  const [detectedLanguageName, setDetectedLanguageName] = useState("");

  // Check for first visit and show auto-detection message
  useEffect(() => {
    if (isFirstVisit()) {
      const detectedLang = detectBrowserLanguage();
      const langName = languageNames[detectedLang]?.name || "English";
      
      // Mark as visited
      localStorage.setItem("washpros-visited", "true");
      
      // If detected language is not English, show notification
      if (detectedLang !== "en") {
        setAutoDetected(true);
        setDetectedLanguageName(langName);
        
        // Auto-dismiss after 5 seconds — return cleanup so React can
        // cancel the pending state update if the effect unmounts.
        const dismissTimer = setTimeout(() => {
          setAutoDetected(false);
        }, 5000);
        return () => clearTimeout(dismissTimer);
      }
    }
  }, []);

  useEffect(() => {
    // Sync with both localStorage keys for compatibility
    localStorage.setItem("washpros-language", language);
    localStorage.setItem("washpros_language", language);

    // Phase 7g.15.57 — dynamic <html lang> + hreflang meta.
    // Browsers, screen readers, and search engines all key off the
    // document-language attribute. Phase 7g.15.108 (2026-06-17)
    // expanded coverage from EN+ES to the full 11-locale matrix +
    // additional Spanish-market specificity for Florida (es-MX,
    // es-PR, es-CU) so Google can surface the spot to the right
    // hispanophone audience.
    const htmlLangMap = {
      en: "en-US", es: "es-US", fr: "fr-FR", pt: "pt-BR", de: "de-DE",
      zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", it: "it-IT", ru: "ru-RU",
      ht: "ht-HT",
    };
    document.documentElement.lang = htmlLangMap[language] || "en-US";

    // Build hreflang link tags (and the x-default fallback) so Google
    // can offer the right locale to the right user. We host every
    // locale at the SAME URL (the lang gate is the runtime context),
    // so each hreflang points at the current `location.href`.
    // Florida-specific Spanish variants (es-MX, es-PR, es-CU) tell
    // Google to surface the Spanish version to Mexican, Puerto Rican,
    // and Cuban-American searchers — the three largest Hispanic
    // sub-markets we operate in.
    try {
      const head = document.head;
      const baseHref = window.location.href.split("#")[0];
      const langs = [
        { lang: "en-US", href: baseHref },
        { lang: "es-US", href: baseHref },
        { lang: "es-MX", href: baseHref },
        { lang: "es-PR", href: baseHref },
        { lang: "es-CU", href: baseHref },
        { lang: "fr-FR", href: baseHref },
        { lang: "fr-CA", href: baseHref },
        { lang: "pt-BR", href: baseHref },
        { lang: "pt-PT", href: baseHref },
        { lang: "de-DE", href: baseHref },
        { lang: "zh-CN", href: baseHref },
        { lang: "ja-JP", href: baseHref },
        { lang: "ko-KR", href: baseHref },
        { lang: "it-IT", href: baseHref },
        { lang: "ru-RU", href: baseHref },
        { lang: "ht-HT", href: baseHref },
        { lang: "x-default", href: baseHref },
      ];
      // Drop any previous hreflang tags we wrote
      head.querySelectorAll('link[rel="alternate"][data-i18n-hreflang]')
        .forEach((n) => n.remove());
      // Inject fresh ones
      for (const { lang, href } of langs) {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", href);
        link.setAttribute("data-i18n-hreflang", "1");
        head.appendChild(link);
      }

      // Phase 7g.15.108 — also write Open Graph locale tags so social
      // crawlers (Facebook, LinkedIn, X, Slack unfurls) can pick the
      // right preview locale. og:locale gets the active language;
      // og:locale:alternate is repeated for every other supported one.
      head.querySelectorAll('meta[data-i18n-og-locale]').forEach((n) => n.remove());
      const ogLocale = htmlLangMap[language] || "en-US";
      const ogPrimary = document.createElement("meta");
      ogPrimary.setAttribute("property", "og:locale");
      ogPrimary.setAttribute("content", ogLocale.replace("-", "_"));
      ogPrimary.setAttribute("data-i18n-og-locale", "1");
      head.appendChild(ogPrimary);
      Object.entries(htmlLangMap)
        .filter(([k]) => k !== language)
        .forEach(([, v]) => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:locale:alternate");
          m.setAttribute("content", v.replace("-", "_"));
          m.setAttribute("data-i18n-og-locale", "1");
          head.appendChild(m);
        });
    } catch {
      /* document.head not ready (e.g. SSR fallback) — ignore */
    }

    // Also update HTML dir attribute for RTL languages (future support)
    // document.documentElement.dir = ['ar', 'he'].includes(language) ? 'rtl' : 'ltr';
  }, [language]);

  const t = useCallback((key, params) => {
    // Phase 7g.15.92 (2026-06-07): accept a 2nd `params` object so the
    // i18n value's `{{var}}` placeholders get interpolated. Without
    // this, calls like t('customer_dashboard.unread_label', {count: 9})
    // returned the literal "{{count}} unread" string to the DOM —
    // visible in the bottom-nav aria-label and (worse) in any UI that
    // surfaced the raw value.
    let value;
    if (translations[language]?.[key]) value = translations[language][key];
    // Phase 7g.15.36 — vendor portal i18n module (keeps LanguageContext lean)
    else if (VENDOR_PORTAL_I18N[language]?.[key]) value = VENDOR_PORTAL_I18N[language][key];
    // Phase 7g.15.48 — admin front-page + Control Center i18n module
    else if (ADMIN_FRONT_PAGE_I18N[language]?.[key]) value = ADMIN_FRONT_PAGE_I18N[language][key];
    else if (translations.en?.[key]) value = translations.en[key];
    else if (VENDOR_PORTAL_I18N.en?.[key]) value = VENDOR_PORTAL_I18N.en[key];
    else if (ADMIN_FRONT_PAGE_I18N.en?.[key]) value = ADMIN_FRONT_PAGE_I18N.en[key];
    else {
      // i18next fallback — handles nested keys like "nav.home"
      try {
        if (i18nInstance?.exists?.(key)) {
          // i18next handles `params` natively — pass through.
          return i18nInstance.t(key, params || {});
        }
      } catch {
        /* i18next not ready */
      }
      value = key;
    }

    // Interpolate `{{var}}` placeholders if params were provided.
    if (params && typeof value === "string" && value.indexOf("{{") !== -1) {
      return value.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
        return params[name] != null ? String(params[name]) : "";
      });
    }
    return value;
  }, [language]);

  const setLanguage = useCallback((newLang) => {
    if (supportedLanguages.includes(newLang)) {
      setLanguageState(newLang);
      setAutoDetected(false);
      try {
        if (i18nInstance?.changeLanguage) {
          i18nInstance.changeLanguage(newLang);
        }
      } catch {
        /* i18next not ready */
      }
    }
  }, []);

  const dismissAutoDetected = useCallback(() => setAutoDetected(false), []);

  const contextValue = useMemo(() => ({ 
    language, 
    setLanguage, 
    t,
    languages: supportedLanguages,
    languageNames,
    autoDetected,
    detectedLanguageName,
    dismissAutoDetected
  }), [language, setLanguage, t, autoDetected, detectedLanguageName, dismissAutoDetected]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageContext;
